// Shared types for the AI Adoption (AAI) feature — mirrors the backend
// responses in app/routes/aai/{readiness,onboarding}.py.

export const AAI_DIMENSIONS = [
  'literacy',
  'tool_adoption',
  'usage_depth',
  'workflow',
  'output_impact',
] as const;
export type AaiDimension = (typeof AAI_DIMENSIONS)[number];

export const AAI_STAGES = ['nascent', 'early', 'developing', 'mature', 'advanced'] as const;
export type AaiStage = (typeof AAI_STAGES)[number];

export const DIMENSION_LABELS: Record<AaiDimension, string> = {
  literacy: 'AI literacy',
  tool_adoption: 'Tool adoption',
  usage_depth: 'Usage depth',
  workflow: 'Workflow sophistication',
  output_impact: 'Output impact',
};

// A confidence-weighted range — never a hard score (R2).
export interface DimensionRange {
  low: number;
  high: number;
  confidence?: number;
}

export interface ReadinessSnapshot {
  dimensions: Record<string, DimensionRange> | null;
  stage: AaiStage | string;
  confidence: number | null;
  source?: string;
  measured_at?: string | null;
}

export interface BriefField {
  value: unknown;
  confidence: number;
  source: string;
  label: string;
}

export interface ContextBrief {
  fields: Record<string, BriefField>;
  updated_at?: string | null;
}

export interface ReadinessResponse {
  email: string;
  readiness: ReadinessSnapshot | null;
  brief: ContextBrief | null;
}

export type ViewScope = 'company' | 'department' | 'self';

export interface Workspace {
  id: string;
  name: string | null;
  display_name: string | null;
}

// A reusable program blueprint (saved from a client's curriculum, or a built-in).
export interface ProgramTemplate {
  template_key: string;
  name: string;
  description: string | null;
  tag: string | null;
}

export interface AaiCapabilities {
  view_company_dashboard: boolean;
  view_department_rollup: boolean;
  view_individual: boolean;
  manage_members: boolean;
  manage_program: boolean;
  manage_departments: boolean;
}

export interface AaiContext {
  company_id: string | null;
  // The caller's own membership facts (from /aai/me) — company they belong to,
  // where they sit, and whether the membership is still an open invite. Present
  // for every role, not just the scopes that can read a rollup.
  company_name?: string | null;
  email: string;
  workspace_role: string | null;
  system_role: string;
  scope: ViewScope;
  department: string | null;
  department_id?: string | null;
  membership_status?: 'invited' | 'active' | 'removed' | null;
  readiness_complete?: boolean;
  /** Every permission the caller holds in this company (G3). The sidebar and
   *  the route guard read this, so a grant change in the roles page takes
   *  effect on the next load instead of needing a deploy. */
  permissions?: string[];
  capabilities: AaiCapabilities;
  // Provider-lane flags (from /aai/me) — drive the role-aware /projects page.
  is_provider?: boolean;
  is_provider_owner?: boolean;
  is_builder_admin?: boolean;
  is_expert?: boolean;
  /** Belongs to the main provider (Agents Anywhere), the one that sees every company. */
  is_main_provider?: boolean;
  serving_provider_name?: string | null;
  /** The provider company this person owns: where their Clients tab adds and lists clients. */
  provider_id?: string | null;
}

// ── Training (MCQ / quiz) ───────────────────────────────────────────────────

// A module's link to one isolated platform feature (AA Standard anchor).
export interface FeatureLink {
  key: string;
  label: string;
  route: string;
  live: boolean; // false → anchor feature not built yet; resolves to closest surface
}

export interface ReadingSection {
  h: string;
  p: string;
}

export type ModuleKind = 'quiz' | 'link' | 'reading' | 'homework' | 'micro_learning';
export type TeachingMethod = 'explainer' | 'story' | 'analogy';
export type DeliveryMode = 'group' | 'solo';

// A video attached to a step (Trainer Framework §3), resolved for the viewer.
export interface AaiVideo {
  id: string;
  scope: 'standard' | 'client';
  title: string;
  url: string;
  provider: 'url' | 'youtube' | 'vimeo';
  duration_sec: number | null;
}
export interface VideoLibrary {
  standard: AaiVideo[];
  client: AaiVideo[];
}

export interface TrainingModule {
  id: string;
  title: string;
  description: string | null;
  phase: string | null;
  phase_order?: number;
  week_start: number | null;
  week_end: number | null;
  question_count: number;
  kind: ModuleKind | string;
  method?: TeachingMethod | null;      // teaching style (§3) — presentation only
  delivery_mode?: DeliveryMode | null; // group vs solo (§3)
  feature: FeatureLink | null;
  auto_complete?: boolean; // link step completed by real activity, not by opening
  in_tier?: boolean;       // false → locked in this program's tier (upsell)
  body?: ReadingSection[]; // reading / micro-learning modules only
  instructions?: string;   // homework only
  submit_type?: 'text' | 'link' | 'none'; // homework only
  video?: AaiVideo | null; // attached video, resolved for the participant
  video_ref?: string | null;
  recommended: boolean; // matches the participant's readiness stage (never a gate)
  status: 'not_started' | 'completed';
  score: number | null;
  passed: boolean | null;
  attempts: number | null;
  best_score: number | null;
  /** FND-1: the real thing this lesson leaves behind (null = decorative). */
  artifact?: string | null;
  /** DEL-1: when the participant completed it (self-guided stamp). */
  completed_at?: string | null;
  /** SKL-8: the marketplace skill this lesson starts from ("customise this"). */
  starter_skill?: { id: string; name: string; problem: string | null; process_steps: string[] } | null;
  /** GAM-3: what the participant's tools unlock in the marketplace. */
  unlocks?: { total_published: number; per_tool: Array<{ tool: string; skills: number }> } | null;
}

export interface MyTraining {
  readiness_complete: boolean;
  stage: string | null; // participant's current readiness stage
  modules: TrainingModule[];
  tier?: string; // '3mo' | '6mo' | '12mo'
  summary: { total: number; completed: number };
}

export interface QuizQuestion {
  q: string;
  options: string[];
}

export interface Quiz {
  id: string;
  title: string;
  description: string | null;
  pass_score: number;
  questions: QuizQuestion[];
  attempt: {
    status: string; score: number | null; passed: boolean | null;
    attempts?: number; best_score?: number | null;
  } | null;
}

export interface QuizResult {
  score: number;
  passed: boolean;
  correct: number;
  total: number;
  correct_flags: boolean[];
  attempts: number;
  best_score: number;
}

// Trainer-authored quiz body stored in aai_module.content (kind === 'quiz').
export interface QuizContentQuestion {
  q: string;
  options: string[];
  answer: number; // index into options
}
export interface QuizContent {
  kind: 'quiz';
  pass_score: number;
  stages?: string[]; // readiness stages this quiz is recommended for
  questions: QuizContentQuestion[];
}

// ── Onboarding flow ────────────────────────────────────────────────────────

export interface TransparencyScope {
  scope: string;
  label: string;
  detail: string;
}

export interface TransparencyNotice {
  version: string;
  consent_type: string;
  headline: string;
  body: string;
  scopes: TransparencyScope[];
}

// ── Orbit multimodal onboarding (config-driven, talk-or-tap) ─────────────────

export type OnboardingQType = 'single' | 'multi' | 'scale' | 'open' | 'confirm';

export interface OnboardingOption { value: string; label?: string; }
export interface OnboardingScale { min_label: string; max_label: string; points: number; }
export interface OnboardingPhase { id: string; title: string; index: number; total: number; }

export interface OnboardingQuestion {
  id: string;
  type: OnboardingQType;
  multi: boolean;
  orbit_voice: string;  // what Orbit says (also `say` at the top level)
  say: string;
  phase: OnboardingPhase;
  required: boolean;
  locked?: boolean;     // the universal readiness questions (Q14-18)
  why?: string;         // "Why I ask —" rationale
  payoff?: string;      // ONB-2: what answering this step unlocks (shown after)
  options?: OnboardingOption[];
  spoken?: string[];    // the option labels voice reads aloud (get a blue dot)
  scale?: OnboardingScale;
  open?: { chips: string[]; placeholder: string };
  explicit_confirm?: boolean;
}

export interface OnboardingRecap {
  role: string;
  top_time_sink: string | null;
  handoff: string | null;
  stage: string | null;
  say: string;
  buttons: string[];
}

// /onboarding/state and /onboarding/turn return one of these shapes.
export interface OnboardingWelcome { voice: string; buttons: string[]; }

export interface OnboardingState {
  consent_required?: boolean;
  notice?: TransparencyNotice;
  done?: boolean;
  question?: OnboardingQuestion;
  recap?: OnboardingRecap;
  readiness?: ReadinessSnapshot | null;
  // Shown once before the first question (welcome / connect-later framing).
  welcome?: OnboardingWelcome;
  // What Orbit speaks aloud for the current step. Present on every shape.
  say?: string;
  // ONB-2 (turn responses only): the payoff the just-ACCEPTED answer unlocked.
  // Absent when the answer was rejected and the same question is re-asked.
  payoff?: string | null;
  // Turn responses only: why the last answer was not accepted (question re-asked).
  note?: string | null;
  // A booked Orbit call for this check-in, or null when none is pending.
  call_booking?: OnboardingCallBooking | null;
  // Number to prefill the booking form with (only sent when nothing is booked).
  suggested_phone?: string | null;
}

// Orbit rings the participant at `scheduled_for` and walks the same questions.
export interface OnboardingCallBooking {
  call_id: string;
  scheduled_for: string | null;
  to_number: string | null;
  status: string;
}

// The structured answer for the current question.
export type OnboardingAnswer = string | string[] | number | null;

// What the browser needs to open an in-browser ElevenLabs voice session.
export interface VoiceConnection {
  signed_url: string;
  dynamic_variables: Record<string, string | number | boolean>;
  first_message?: string; // onboarding greeting override for the shared Orbit agent
}

// ── Aggregates (rollups / dashboard) ────────────────────────────────────────

export interface Aggregate {
  onboarded_count: number;
  stage_distribution: Record<string, number> | null;
  dimensions: Record<string, DimensionRange> | null;
  suppressed: boolean;
  suppressed_reason?: string;
  maturity?: MaturityScore;   // present on /dashboard (Feature 2)
}

export interface DepartmentAggregate extends Aggregate {
  department: string;
}

export interface CompanyDashboard {
  company_id: string;
  enrolled: number;
  company: Aggregate;
  departments: DepartmentAggregate[];
}

export interface RosterMember {
  email: string;
  name: string;
  department: string | null;
  workspace_role: string | null;
  onboarded: boolean;
  /** Open requests they raised and published skills they made. */
  working_on?: PersonWork;
}

export interface PersonWork {
  requests: { id: string; title: string; state: string }[];
  skills: { id: string; name: string }[];
  total: number;
}

export type CompanyDocumentLevel = 'company' | 'department' | 'person';

export interface CompanyDocument {
  id: string;
  level: CompanyDocumentLevel;
  department: string;
  owner_email: string;
  filename: string;
  content_type: string;
  bytes: number;
  uploaded_by: string;
  created_at: string;
}

export interface CompanyDocumentLibrary {
  documents: CompanyDocument[];
  can: { company: boolean; department: string[]; person: boolean };
  department?: string;
}

export interface RosterResponse {
  scope: ViewScope;
  department: string | null;
  members: RosterMember[];
}

// ── Role-based home dashboards (GET /api/aai/home) ──────────────────────────

export type JourneyStatus = 'complete' | 'in_progress' | 'current' | 'locked';
export interface JourneyStep { key: string; label: string; status: JourneyStatus; pct: number; note: string; }
export interface TimeRange { low: number; high: number; unit: string; }

export interface RequestAssignee {
  email: string;
  added_by: string;
  added_at: string | null;
}

/* One project's room: what people put into it, and what the work produced.
   Both live in the builder, which is where a project's bytes are; this is the
   read of them, never a second copy. */
export interface ProjectDocument {
  id: string;
  name: string;
  path: string;
  size: number;
  mime: string | null;
  available: boolean;
  created_at: string | null;
}

export interface GeneratedDocument {
  id: string;
  kind: string;
  title: string;
  version: number;
  status: string;
  conversation_id: string | null;
  updated_at: string | null;
}

export interface ProjectDocuments {
  project_id: string | null;
  uploaded: ProjectDocument[];
  generated: GeneratedDocument[];
  screenshots: number;
  /* Why the room is empty, when it is. Null when there is nothing to explain. */
  reason: string | null;
}

/** One project's room inside the company-wide Documents read. */
export interface CompanyDocumentRoom {
  request_id: string;
  title: string;
  project_id: string | null;
  /** True when the Workspace could not be asked. Different from an empty room. */
  unreadable: boolean;
  uploaded: ProjectDocument[];
  generated: GeneratedDocument[];
  screenshots: number;
}

/** Everything the caller can reach, across their work. */
export interface CompanyDocuments {
  projects: CompanyDocumentRoom[];
  files: number;
  produced: number;
  stored_bytes: number;
  /** How many projects were asked, including the ones that held nothing. */
  projects_total: number;
  unreadable: number;
}

export interface RequestDisplay {
  /** The StatusChip state: pending, needs_review, in_progress, in_delivery, declined, ready_short. */
  chip: string;
  chip_label: string;
  /** The most specific state known: the Orbit stage, Built, Ready to use, or the chip's words. */
  state: string;
  next: string;
}

export interface AaiRequest {
  id: string;
  requester_email: string;
  requester_name?: string;
  department: string | null;
  title: string;
  detail: string | null;
  risk: string;
  status: string;            // pending | needs_info | dept_approved | approved | declined
  decided_by: string | null;
  decided_at: string | null;
  created_at: string | null;
  problem_id: string | null; // set when approved → seeded into the scoping pipeline
  model: string | null;          // governance: model powering the approved use-case
  human_in_loop: string | null;  // governance: human-in-loop policy
  capability_tag?: string | null;
  /* What Ask Orbit recommended at intake. Null on anything that was never
     routed, which is not the same as a Quick Win. */
  route?: 'quick_win' | 'project' | null;
  /* What Orbit recommends we BUILD. `route` says how big, this says what shape.
     Null means nobody classified it, and is never read as 'skill'. */
  deliverable?: 'skill' | 'plugin' | 'document' | 'routine' | null;
  /** Why it is that shape, in the words the intake rule used. */
  deliverable_reason?: string | null;
  owner_email?: string | null;
  target_phase?: string | null;
  scope_notes?: string | null;
  eu_ai_act_class?: string | null;
  nist_function?: string | null;
  iso_control?: string | null;
  tool_vendor?: string | null;
  source?: string;               // request | shadow_ai
  // I2: the named person delivering it. Null means nobody has been named, and
  // the PRD-ready ping still goes to the whole expert roster.
  assigned_expert_email?: string | null;
  assigned_expert_by?: string | null;
  assigned_expert_at?: string | null;
  /* Who the request is with right now, and at which gate. Derived from the
     status, never stored: a stored reviewer goes stale the moment a department
     head changes. Null on a request that is settled. */
  reviewer_email?: string | null;
  reviewer_gate?: 'department' | 'company' | null;
  /* How the request reads, from the server's one table (request_status.py):
     every list and drawer shows these rather than working them out again. */
  display?: RequestDisplay;
  /* The gate it sits at is the reader's, or it was sent back to them. */
  needs_you?: boolean;
  /* Everyone currently on it. `assigned_expert_email` is the primary builder
     and is also in `builders`. */
  scopers?: RequestAssignee[];
  builders?: RequestAssignee[];
  tool_skill_id?: string | null; // published library skill delivered for this request
  est_cost_band?: string | null; // financial lens: setup cost as a range, when recorded
  est_value_band?: string | null; // executive lens: value as a range, a forecast until confirmed
  problem_stage?: string | null; // Orbit stage of the linked problem card, for owner-scoped work
  /* The builder's own id for this project, when a build exists. It is what the
     Planner opens on, so a null here means there is nothing to continue yet,
     not that the person lacks access. */
  builder_project_id?: string | null;
  build_state?: 'built' | 'tested' | null; // last build signal from the Builder or a person
  build_state_at?: string | null;
  build_state_source?: string | null;
  // Set only on work named to you in a company other than the one you are
  // signed into (a delivery expert's client work). Absent means your own company.
  company_id?: string | null;
  company_name?: string | null;
}

export type CapabilityTag = 'summarization' | 'drafting' | 'analysis' | 'automation' | 'other';

// ── Skills Marketplace (Feature 4) ─────────────────────────────────────────
export type SkillAccessLevel = 'open' | 'request' | 'restricted';
export type MySkillAccess = 'open' | 'granted' | 'requested' | 'denied' | 'none';
export interface AaiSkill {
  id: string;
  name: string;
  department?: string | null;
  problem: string | null;
  process_steps: string[];
  example_input: string | null;
  example_output: string | null;
  access_level: SkillAccessLevel;
  published: boolean;
  use_case_id: string | null;
  use_case_title?: string | null;
  created_by: string | null;
  created_at: string | null;
  my_access?: MySkillAccess;
  /* Where it came from, which is how the marketplace groups: 'provider' is ours
     and readable by every company, 'company' is this company's own. */
  source?: 'provider' | 'company';
  /* What to call it. Derived from kind: anything that is not a plugin is a
     skill, including older rows that carry no kind at all. */
  shape?: 'skill' | 'plugin';
  /* What it IS, as stored. `shape` is read from this; the editor writes it. */
  kind?: 'context' | 'workflow' | 'plugin' | null;
  /* What it needs connected. For a plugin this is its identity, not a detail. */
  tools?: string[];
}
export interface SkillAccessRow {
  member_email: string;
  status: string;
  decided_by: string | null;
  decided_at: string | null;
  requested_at: string | null;
}
export interface SkillUsage {
  total_uses: number;
  unique_users: number;
  last_used: string | null;
  by_member?: { member_email: string; uses: number; last_used: string | null }[];
}

// ── Vendor / Tool Evaluator (Feature 5) ─────────────────────────────────────
export interface VendorEntry {
  id: string;
  evaluation_id: string;
  name: string;
  what_it_does: string | null;
  technical_fit: string | null;
  cost_analysis: string | null;
  pricing_tier: string | null;
  soc2: string | null;
  data_retention: string | null;
  data_flow: string | null;
  eu_ai_act_class: string | null;
  controls: string | null;
  alternatives: string | null;
  overlap_note: string | null;
  rating: number | null;
  prefilled: boolean;
  sort_order: number;
}
export interface PlaybookStep {
  text: string;
  done: boolean;
}
export interface VendorEvaluation {
  id: string;
  title: string;
  framework_note: string | null;
  recommendation: string | null;
  status: string;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  playbook: PlaybookStep[];
  announced_at: string | null;
  announced_by: string | null;
  tools?: string[];              // vendor names (list cards)
  vendor_count?: number;
  vendors?: VendorEntry[];
}
export interface VendorResearch {
  available: boolean;
  prefilled: boolean;
  overlap_note: string | null;
  fields: Partial<VendorEntry> & { name: string };
  note: string;
}

export interface RegistryRow {
  id?: string;
  name: string;
  department: string;
  risk: string;
  capability_tag: string | null;
  model: string;
  human_in_loop: string;
  data_flow: string;             // technical lens: where data goes / residency
  est_cost_band: string;         // financial lens: cost as a range
  est_value_band: string;        // executive/financial lens: value as a range
  owner: string;
  target_phase: string;
  eu_ai_act_class: string | null;
  nist_function: string | null;
  iso_control: string | null;
  tool_vendor: string;
  source: string;                // request | shadow_ai
  status: string;                // Live | Shadow AI | Blocked
  scope_notes: string | null;
  requester_email: string | null;
  created_at: string | null;
  approved_by: string | null;    // examiner lens: final-gate provenance
  approved_at: string | null;
  evaluation_id?: string | null;      // linked vendor evaluation for this tool
  evaluation_status?: string | null;
  overlap?: string | null;            // shelfware note from the linked evaluation
}

export interface DiscoveredTool {
  tool: string;
  count: number;
}

export interface ParticipantHomeData {
  role: 'participant';
  department: string | null;
  readiness: ReadinessSnapshot | null;
  trend: { previous_stage: string | null; improved: boolean };
  time_redirected: TimeRange;
  journey: JourneyStep[];
  skills: { name: string }[];
  recommendations: { id: string; name: string; problem: string | null; reason: string; access_level: string }[];
  next_session: AaiSession | null;
  pending_requests: AaiRequest[];
  min_group_size: number;
}

export interface DeptHeadHomeData {
  role: 'department_head';
  department: string | null;
  stats: { dept_stage: string | null; onboarded: number; total: number; time_redirected: TimeRange; suppressed: boolean };
  journey_bars: { label: string; val: string; pct: string }[];
  people: { name: string; initials: string; stage: string | null; onboarded: boolean }[];
  approvals: AaiRequest[];
  min_group_size: number;
}

export interface MaturityScore {
  maturity_pct: number | null;   // null when suppressed (< min group size)
  band: string | null;
  scored: number;
  suppressed: boolean;
}
export interface ScoringConfig {
  weights: Record<string, number>;
  thresholds: Record<string, number>;
  customized: boolean;
  dimensions: string[];
  stages: string[];
}
export interface DashboardTrend {
  suppressed: boolean;
  points: { week: string; maturity_pct: number; measured: number }[];
}

export interface ChampionHomeData {
  role: 'champion';
  company: Aggregate;
  company_bars: { label: string; val: string; pct: string; note: string }[];
  maturity: MaturityScore;
  departments: { department: string; band: string | null; onboarded: number; pct: string; maturity_pct: number | null; suppressed: boolean }[];
  spotlights: { who: string; what: string }[];
  registry: RegistryRow[];
  approvals: AaiRequest[];   // gate 2 (dept_approved) + deptless gate-1 requests
  enrolled: number;
  min_group_size: number;
}

export interface ConsultantHomeData {
  role: 'consultant';
  stats: { engagements: number; people: number; sessions: number; graduated: number };
  clients: { id: string; name: string; initials: string; meta: string; stage: string; pct: string }[];
  templates: { key: string; name: string; meta: string; tag: string }[];
  delivery: { day: string; title: string; client: string }[];
  min_group_size: number;
}

export interface OwnerHomeData {
  role: 'owner';
  stats: { members: number; seats: number; in_program: number; onboarded: number; plan: string; aai_enabled: boolean; maturity_pct: number | null; maturity_suppressed: boolean };
  team: { name: string; initials: string; email: string; department: string; role: string; system: string }[];
  billing: { plan: string; seats: number } | null;
  approvals: AaiRequest[];   // gate 2 (dept_approved) + deptless gate-1 requests
  min_group_size: number;
}

// ── PRD reviews (Expert role — the engineer gate before builds) ─────────────
export type PrdReviewStatus = 'pending' | 'changes_requested' | 'approved';
export interface PrdReviewState {
  status: PrdReviewStatus;
  reviewer_email: string | null;
  comment: string | null;
  decided_at: string | null;
  history: { status: string; reviewer_email: string | null; comment: string | null; decided_at: string | null }[];
}
export interface PrdReviewSummary {
  engagement_id: string;
  engagement_status: string;
  title: string;
  drafted_at: string | null;
  section_count: number;
  validx_project_id: string | null;
  created_at: string | null;
  request_title: string | null;    // set when the PRD came from an AAI request
  requester_email: string | null;
  review: PrdReviewState;
}
export interface PrdReviewDetail extends PrdReviewSummary {
  prd: { markdown: string | null; sections: Record<string, unknown> };
}
export interface PrdReviewList {
  reviews: PrdReviewSummary[];
  counts: { pending: number; changes_requested: number; approved: number };
}

export interface ExpertHomeData {
  role: 'expert';
  company_id: string | null;
  counts: { pending: number; changes_requested: number; approved: number };
  reviews: PrdReviewSummary[];
  min_group_size: number;
}

export type AaiHome =
  | ParticipantHomeData
  | DeptHeadHomeData
  | ChampionHomeData
  | ConsultantHomeData
  | OwnerHomeData
  | ExpertHomeData;

// ── Insight sub-pages (Phase A — derived reads) ─────────────────────────────

export interface ProductivityData {
  series: { measured_at: string | null; stage: string; midpoint: number | null }[];
  current_stage: string | null;
  hours_per_week: TimeRange;
  improved: boolean;
}
export interface MyToolsData {
  tools: { value: string; label: string }[];
  ai_tools: { value: string; label: string }[];
  skills: { id?: string; name: string }[]; // skills the person built themselves (Module 3)
  built_tools: { id: string; name: string; request_title: string }[]; // delivered from your requests
  learning_pref: string | null;
  onboarded: boolean;
}

// ── Module 3: Skills (guided builder) ───────────────────────────────────────

// A draft skill (context or workflow), assembled from the onboarding brief.
export interface SkillPrefill {
  name: string;
  problem: string;
  process_steps: string[];
  example_input: string; // context: the "about me" block · workflow: usually empty
  example_output: string;
  has_brief: boolean;
  task?: string; // workflow mode: the task being automated
  /** TD-3: the delegation profile shaping this draft (workflow mode). */
  act_policy?: 'yes' | 'approval' | 'no' | null;
  profile_decided?: number;
}

// ── Module 4: Build & Vet (versioning + run feedback) ───────────────────────
export interface SkillVersion {
  version_no: number;
  snapshot: Record<string, unknown>;
  note: string | null;
  created_at: string | null;
}
export type RunOutcome = 'worked' | 'partial' | 'failed';
export interface SkillRun {
  id: string;
  outcome: RunOutcome;
  note: string | null;
  version_no: number | null;
  ran_at: string | null;
}
export interface VettingStatus {
  runs: number;
  worked: number;
  versions: number;
  vetted: boolean;
  target: { worked: number };
  /** Real runs of this skill you have not yet reported an outcome for. Zero
   *  means the outcome buttons are not available: saying "it worked" used to be
   *  a click, so three clicks graduated a skill nobody had ever used. */
  unreported?: number;
}
export interface VettingData {
  vetting: VettingStatus;
  runs: SkillRun[];
  versions: SkillVersion[];
}

// ── Module 5: Team Adoption (graduation + org chart + mentorship) ───────────
export interface SkillEffectiveness {
  runs: number; worked: number; versions: number; vetted: boolean;
  success_rate: number | null; adoption: number;
}
export interface Graduation {
  id: string;
  skill_id: string;
  skill_name: string;
  from_scope: string;
  to_scope: string;
  department: string | null;
  status: 'proposed' | 'approved' | 'declined';
  proposed_by: string;
  decided_by: string | null;
  note: string | null;
  created_at: string | null;
  decided_at: string | null;
  effectiveness?: SkillEffectiveness; // present only in the champion queue
  /** SKL-9: the recorded run history riding the approval request. */
  evidence?: {
    runs: number; worked: number; versions: number;
    recent_runs: Array<{ outcome: string; note: string | null; at: string | null }>;
  };
}
export interface OrgMember {
  email: string;
  name: string;
  adoption_role: string;
  /** ROL-3: HR-supplied (or self-confirmed) job title, when known. */
  job_title?: string | null;
  is_coach: boolean;
  onboarded: boolean;
}
/** A department node in HR's org hierarchy (department.parent_id). */
export interface OrgDeptNode {
  name: string;
  members: OrgMember[];
  children: OrgDeptNode[];
}
export interface OrgEdge {
  id: string; coach_email: string; learner_email: string; coach_name: string; learner_name: string;
}
export interface OrgChartData {
  departments: OrgDeptNode[];
  unassigned: OrgMember[];
  edges: OrgEdge[];
}
export interface Mentorship {
  id: string; coach_email: string; learner_email: string; status: string; created_at: string | null;
}
export interface TeamBundle {
  department: string; count: number; skills: { id: string; name: string }[];
}

// ── Module 6: Measure, Certify & Scale (impact + certification) ─────────────
export interface Certification {
  cert_key: string; label: string; issued_at: string | null;
}
export interface ImpactTopSkill {
  name: string; department: string | null; adoption: number; success_rate: number | null;
}
export interface SkillImpact {
  ip_counts: { personal: number; team: number; company: number; vetted: number };
  graduations: { proposed: number; approved: number; declined: number };
  shared_performance: { avg_success_rate: number | null; total_runs: number; adoption: number };
  top_skills: ImpactTopSkill[];
}
export interface DeptPack {
  department: string; count: number; adoption: number; skills: string[];
}
export interface ImpactRoi {
  suppressed: boolean;
  maturity_pct: number | null;      // 0–100, null when suppressed
  maturity_band: string | null;
  onboarded: number;
  time_redirected: TimeRange;        // monthly, company-scaled (a range, never a hard number)
  velocity: {
    suppressed: boolean;
    points: { week: string; maturity_pct: number; measured: number }[];
    delta_pts: number | null;        // last week vs first week
  };
}
export interface ImpactData {
  skills: SkillImpact;
  dept_packs: DeptPack[];
  roi?: ImpactRoi;   // ROI headline (design's impact dashboard); optional for older backends
}

export interface PersonalSkill {
  id: string;
  name: string;
  problem: string | null;
  process_steps: string[];
  example_input: string | null;
  example_output: string | null;
  kind: string | null;
  scope: string;
  author_email: string | null;
}

// ── Module 2: Tool Intuition (library + tool dives) ─────────────────────────

// A read-only tool card from the company's published evaluations.
export interface ToolLibraryEntry {
  name: string;
  what_it_does: string | null;
  pricing_tier: string | null;
  soc2: string | null;
  data_flow: string | null;
  rating: number | null;
  evaluation_title: string;
  recommendation: string | null;
  cleared: boolean; // published + announced = formally rolled out
}

export interface DiveStep {
  key: string;
  label: string;
  /** Derived server-side from real state (connection + delegation answers, a
   *  turn you took, an action that actually ran, your notes). Not settable:
   *  these were checkboxes, so a dive could reach 4 of 4 without any of it
   *  being true. */
  done: boolean;
  /** The one thing that would tick it, shown on an unticked tile. */
  howto?: string | null;
}
export interface ToolDive {
  id: string;
  tool_name: string;
  status: 'planned' | 'done';
  steps: DiveStep[];
  notes: string | null;
  week_no: number | null;
  dived_at: string | null;
  /** Why this tool is in the program: employer stack vs own, + skills it unlocks. */
  purpose?: { source: 'org' | 'personal' | null; ready_skills: number };
  /** The evidence the steps are read from: turns you took in the try-it lane,
   *  and actions the assistant actually ran in the tool. */
  turns_run?: number;
  actions_run?: number;
}
/** One action the assistant may use in the dive's try-it chat — the caller's
 *  own delegation answers, org-filtered; approval-gated actions pause per run. */
export interface OfferedAction {
  key: string; label: string; risk: string; description: string;
  decision: 'yes' | 'approval'; requires_approval: boolean;
}
export interface DiveTurnResult {
  reply: string;
  tool: string;
  offered: { connected: boolean; reason: string | null; actions: OfferedAction[] };
  pending_approval: {
    key: string; label: string; risk: string;
    // args_preview is the raw provider JSON (kept as a fallback); `summary` is
    // the same call in plain language, which is what the consent card shows.
    args_preview: string;
    summary?: { label: string; value: string }[];
    why?: string;
  } | null;
  tool_activity: Array<{ action: string; label: string; ok: boolean }>;
}
export interface DiveTrackerData {
  dives: ToolDive[];
  suggestions: string[];
  /** TD-5: the dive queue — the employer's real tools first (IT intake), then
   *  the person's own stack; each entry says where it came from. */
  queue?: Array<{
    tool: string; from: 'org' | 'personal';
    /** Set when `tool` is a generic answer ("Email") rather than a product:
     *  the real connectors behind it, so the UI asks which one. */
    options?: string[];
  }>;
  format: { key: string; label: string }[];
  progress: { done: number; target: number };
}

/** TD-1/2: one capability in the delegation walk. Risk is OUR classification —
 *  the provider's grouping is never trusted. */
export interface DelegationAction {
  key: string;
  label: string;
  description?: string;
  touches?: string;
  risk: 'read' | 'draft' | 'act' | 'destructive';
  source?: string;
  /** TD-4: the policy label when IT switched this off org-wide, else null. */
  blocked?: string | null;
  decision?: 'yes' | 'approval' | 'no' | null;
}
/* One tool, everything needed to govern it: what it can do, what you allow,
   and what breaks if you take an allowance away. */
export interface SkillUsingTool {
  id: string;
  name: string;
  published: boolean;
  scope: string | null;
}

/* Something this person keeps doing in a tool, offered back as a skill. */
export interface ToolRepeat {
  tool: string;
  action_key: string;
  label: string;
  times: number;
  last_used: string | null;
  suggested_name: string;
  suggested_problem: string;
}

export interface ToolControl {
  tool: string;
  source?: string;
  total_available: number;
  actions: DelegationAction[];
  summary: { allowed: number; refused: number; undecided: number };
  /* Empty means nobody has declared a need for this tool, which is not the
     same as nothing using it: a skill built before skills declared their tools
     says nothing either way. */
  skills: SkillUsingTool[];
  switchable_risks: string[];
  repeats?: ToolRepeat[];
}

export interface ToolDelegation {
  tool: string;
  total_available: number;
  source: string;
  actions: DelegationAction[];
  progress: { decided: number; total: number };
}
/** What the company allows an assistant to do in one tool. `person` leaves it to each member's own walk. */
export type CompanyActionLevel = 'person' | 'approval' | 'off';
export interface CompanyToolPolicy {
  tool: string;
  source: string;
  total_available: number;
  actions: Array<DelegationAction & { level: CompanyActionLevel; fixed: string | null }>;
  levels: CompanyActionLevel[];
}
/** TD-3: the profile every skill inherits. */
export interface PermissionProfile {
  tools: Record<string, { yes: Array<{ key: string; label: string; risk: string }>; approval: Array<{ key: string; label: string; risk: string }>; no: Array<{ key: string; label: string; risk: string }> }>;
  decided: number;
  act_policy: 'yes' | 'approval' | 'no' | null;
}
export interface WhatsAllowedData {
  approved_use_cases: { name: string; department: string; risk: string }[];
  tools_in_use: { value: string; label: string; users: number }[];
  // Formally cleared tools — published + announced vendor evaluations.
  approved_tools?: { id: string; title: string; recommendation: string | null; tools: string[]; announced_at: string | null }[];
  guidelines: string[];
}
export interface GovernanceData {
  use_case_count: number;
  by_risk: Record<string, number>;
  framework: string;
  controls: { name: string; detail: string; status: string }[];
  registry: RegistryRow[];
}
export interface ReportsData {
  enrolled: number;
  onboarded: number;
  company_stage: string | null;
  company: Aggregate;
  departments: { department: string; onboarded: number; band: string | null }[];
  use_cases: number;
  dimension_labels: Record<string, string>;
}
export interface HelpData {
  coach: { name: string; email: string } | null;
  resources: { title: string; detail: string }[];
}
export interface ProgramConfig {
  name: string;
  enabled: boolean;
  preset: string;
  phases: { key: string; label: string }[];
  configured: boolean;
  tier: string | null;       // '3mo' | '6mo' | '12mo' | null (defaults to 6-month)
  starts_on: string | null;  // program start date (drives the module calendar)
}

// ── Phase 4/5: curriculum + cohorts + sessions (trainer cockpit) ────────────

export type SessionType = 'coaching' | 'quick-win' | 'deep-dive';
export type PhaseStatus = 'locked' | 'active' | 'complete';

export interface CurriculumModule {
  id: string;
  title: string;
  description: string | null;
  session_type: SessionType;
  content: Record<string, unknown>;
  sort_order: number;
  method?: TeachingMethod | null;
  delivery_mode?: DeliveryMode | null;
  completion?: { attempted: number; passed: number };
  /** FND-1: the real thing this lesson leaves behind (null = decorative). */
  artifact?: string | null;
}
export interface CurriculumPhase {
  id: string;
  phase_key: string;
  title: string;
  description: string | null;
  sort_order: number;
  status: PhaseStatus;
  unlocked: boolean;
  week_start: number | null;
  week_end: number | null;
  in_tier?: boolean;
  start_date?: string | null;
  end_date?: string | null;
  modules: CurriculumModule[];
}

// Company org AI policy (Module 1 acceptable-use step).
export interface AaiPolicy {
  body: string;
  updated_by: string | null;
  updated_at: string | null;
}
export interface Curriculum {
  id: string;
  name: string;
  managed_by: 'trainer' | 'champion';
  handed_off_at: string | null;
  tier?: string | null;
  starts_on?: string | null;
  preset?: string | null;
  setup_pending?: boolean; // first-run: AA-Standard-vs-custom choice not yet made
  phases: CurriculumPhase[];
}
export interface GettingStarted {
  steps: { key: string; label: string; done: boolean; detail: string }[];
  complete: number;
  total: number;
}

export type CohortCut = 'team' | 'availability' | 'readiness' | 'tool_usage';
export interface CohortSuggestion {
  cut_type: CohortCut;
  name: string;
  criteria: Record<string, unknown>;
  members: string[];
}
export interface Cohort {
  id: string;
  name: string;
  cut_type: CohortCut;
  criteria: Record<string, unknown>;
  size: number;
}
export interface CohortDetail extends Omit<Cohort, 'size'> {
  members: string[];
}

export interface AaiSession {
  id: string;
  cohort_id: string | null;
  cohort_name: string | null;
  title: string;
  session_type: SessionType;
  scheduled_at: string | null;
  status: 'planned' | 'completed';
  deliverable_type: string | null;
  deliverable_ref: string | null;
  duration_min?: number | null;
  module_id?: string | null;
  module_title?: string | null; // the step this session covers
  module_week?: string | null;  // "Week N" / "Weeks N–M"
}
export interface MySessions {
  upcoming: AaiSession[];
  all: AaiSession[];
  skills: { name: string }[];
}

// ── In-app notifications (shared delivery path) ──────────────────────────────
export type AaiNotificationKind =
  | 'request_submitted' | 'request_awaiting_final' | 'request_approved'
  | 'request_declined' | 'request_needs_info'
  | 'skill_access_requested' | 'skill_access_granted' | 'skill_access_denied'
  | 'skill_share_proposed'
  | 'request_building' | 'tool_delivered' | 'tool_announced'
  | 'prd_review_requested' | 'prd_changes_requested' | 'prd_approved_by_expert'
  | 'lesson_invite' | 'session_scheduled' | 'control_request'
  | 'portfolio_recommendation' | 'portfolio_response'
  | 'collection_awaiting_release' | 'collection_decided'
  | 'quote_sent' | 'request_built'
  | 'information_requested' | 'connection_requested';

export interface AaiNotification {
  id: string;
  kind: AaiNotificationKind;
  title: string;
  body: string | null;
  link: string | null;
  entity_type: string | null;
  entity_id: string | null;
  read: boolean;
  read_at: string | null;
  created_at: string | null;
  /** The company inbox it sits in; a partner's bell spans every client. */
  client_id?: string | null;
}

export interface AaiNotificationList {
  notifications: AaiNotification[];
  unread_count: number;
}

// ── Phase 1b: first-class departments ───────────────────────────────────────
export interface AaiDepartment {
  id: string;
  name: string;
  lead_email: string | null;
  lead_name: string | null;
  parent_id: string | null;
  members: number;
  onboarded: number;
}


// ── Connectors ──────────────────────────────────────────────────────────────
// A connection is owned: 'personal' belongs to one member, 'organization' is
// explicitly shared. The two states are reported separately per provider and
// never merged, because merging them is what made one person's account look
// like the workspace's.
export type ConnectorScope = 'personal' | 'organization';

export interface ConnectorRow {
  slug: string;
  label: string;
  category: string;
  category_label: string;
  /** false when the organization has switched this connector off. */
  enabled: boolean;
  /** Whether this screen can connect it. Always false for code hosting
   *  (GitHub, GitLab, Bitbucket): git needs a real token in a credential
   *  helper, so that credential belongs to the builder. Server-owned so the UI
   *  cannot drift from what actually works. */
  connect_here: boolean;
  /** Why it can't be connected here, when it can't. Server-owned so the screen
   *  never invents a reason. */
  setup_hint?: string | null;
  /** The CALLER'S OWN connection status, or null. Never a colleague's. */
  personal_status: string | null;
  /** The organization's explicitly shared connection status, or null. */
  organization_status: string | null;
  /** Code hosts only: the Workspace's real connection, since they are connected there. */
  workspace_git?: { connected: boolean; account: string | null; known: boolean };
  /** A toolkit this company added from Composio, not one we ship a tile for. */
  added?: boolean;
  logo?: string;
  /** The Owner's answer on who may use this app. Only on the Owner's own catalog. */
  access?: AppAccess;
  /** 'mcp' for an MCP server; absent for an app. */
  kind?: 'mcp';
  mcp?: { host: string; auth: McpAuth; description: string; connected: boolean };
}

export type McpAuth = 'none' | 'bearer' | 'header' | 'oauth';

/** One of the company's MCP servers. The credential never comes back. */
export interface McpServer {
  key: string;
  label: string;
  description: string;
  host: string;
  auth: McpAuth;
  header: string;
  connected: boolean;
  connected_by: string;
  connected_at: string | null;
  /** What an assistant may change through it: freely, after the person's yes each time, nothing, or each person's own choice. */
  write_level: McpWriteLevel;
  /** After adding or connecting: why the server may not work. It is saved either way. */
  warning?: string | null;
}

export type McpWriteLevel = 'allowed' | 'approval' | 'off' | 'person';

/** A server on the Agents Anywhere list a company can add. */
export interface McpCatalogEntry {
  slug: string;
  key: string;
  label: string;
  url: string;
  host: string;
  auth: McpAuth;
  header: string;
  description: string;
}

export interface McpView {
  servers: McpServer[];
  suggested: McpCatalogEntry[];
  /** Holds the company tools permission, so may add, connect and remove. */
  can_change: boolean;
}

/** A server a person added for themselves alone. */
export interface PersonalMcpServer {
  key: string;
  label: string;
  description: string;
  host: string;
  auth: McpAuth;
  header: string;
  connected: boolean;
  created_at: string | null;
  /** After adding: why the server may not work. It is saved either way. */
  warning?: string | null;
}

/* A person's own answer for one MCP tool: runs on its own, asks each time, or off. */
export type McpToolDecision = 'yes' | 'approval' | 'no';

export interface McpToolPermission {
  name: string;
  title: string;
  description: string;
  read_only: boolean;
  destructive: boolean;
  decision: McpToolDecision | null;
  /** Answered before, no longer listed by the server. */
  unlisted?: boolean;
}

export interface McpPermissionsView {
  key: string;
  label: string;
  tools: McpToolPermission[];
  /** Why the live tool list could not be read; saved answers still show. */
  error: string | null;
}

export interface MyMcpView {
  /** The company's servers this person's sessions already reach. */
  company: { key: string; label: string; host: string }[];
  mine: PersonalMcpServer[];
  can_add: boolean;
}

export interface McpAddBody {
  label: string;
  url: string;
  auth: McpAuth;
  header?: string;
  description?: string;
  secret?: string;
}

/** Phase 3: the tools ONE company named, not the catalog. */
export interface OrbitToolRow {
  key: string;
  label: string;
  category_label: string;
  /** The stack question this tool came out of. */
  named_by: string;
  /** Departments that say they work in this KIND of tool. A guess from the
      category, not a decision, and separate from the assignment below. */
  used_by: string[];
  /** True when the company's own answers named this tool. Separate from the
      assignment: "we run on this" and "it is available here" differ. */
  on_stack: boolean;
  /** The skills that declare they need it, so switching it off has a cost you
      can read before you pay it. */
  used_in_skills: { id: string; name: string; published: boolean; scope: string }[];
  /** Where the Owner said this tool is available. 'unset' means nobody has
      decided, which is not the same as nobody having it. */
  assignment: 'unset' | 'all' | 'selected' | 'owner';
  /** Department names, for reading. Empty when the mode is 'all' or 'unset'. */
  assigned_departments: string[];
  /** The same departments as slugs, for writing the decision back. */
  assigned_slugs: string[];
  /** Teams whose heads switched it off, when it is open to every other department. */
  excluded_departments?: string[];
  /** Null when the company runs on it but Orbit cannot hold a connection. */
  connector: ConnectorRow | null;
  /** Added by this company out of the Composio catalogue, not shipped by us. */
  added?: boolean;
  /** Opportunities against this tool still waiting on somebody. */
  opportunities?: number;
  /** Somebody opened a deep dive on this tool: its own questions are being
      asked. Untouched and empty are different facts, which is why this is a
      flag rather than a count of zero. */
  dived?: boolean;
  /** How many of the tool's own questions have come back. */
  dive_answered?: number;
}

export interface OrbitToolBoard {
  tools: OrbitToolRow[];
  departments: { name: string; slug: string; categories: string[] }[];
  /** What the company named that the catalogue cannot carry. Said rather than
      listed: nothing can be connected, assigned or built on them. */
  unreachable: string[];
  can_use: boolean;
  can_manage: boolean;
}

/** One useful thing Orbit found in a tool and the company around it. */
export interface ToolOpportunity {
  id: string;
  tool_key: string;
  title: string;
  summary: string;
  /** Why it was identified: what was actually read or told to us. */
  evidence: string;
  kind: 'skill' | 'plugin' | 'build';
  department_slug: string;
  department: string;
  /** Who it is for, already worded: a department name, or "Everyone". */
  audience: string;
  proposed: {
    name?: string;
    description?: string;
    instructions?: string;
    steps?: string[];
    example_input?: string;
    example_output?: string;
    never_without_asking?: string[];
    /** Orbit's one-sentence reason for the kind. */
    kind_reason?: string;
  };
  /** 'read' when the tool itself was read; 'context' when it stands on what the
   *  company already told Orbit. The screen says which. */
  source: string;
  status: 'open' | 'converted' | 'building' | 'built' | 'enabled' | 'dismissed';
  skill_id: string | null;
  skill_name: string;
  skill_published: boolean;
  /** What the build workspace reported it finished. */
  built: {
    capability_id?: string | null;
    capability_kind?: string | null;
    project_id?: string | null;
    name?: string;
    description?: string;
  } | null;
  built_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ToolOpportunityList {
  opportunities: ToolOpportunity[];
  counts: Record<string, number>;
  can_manage: boolean;
}

/* Who may use one app: everyone, only the Owner, or no one. */
export type AppAccessMode = 'on' | 'owner' | 'off';
export interface AppAccess {
  mode: AppAccessMode;
}

export interface CodeHostState {
  connected: boolean;
  account: string | null;
  known: boolean;
}

export interface CodeHostStatus {
  github: CodeHostState;
  gitlab: CodeHostState;
  can_connect: boolean;
}

export interface ConnectorCatalog {
  /** May connect their own accounts. */
  can_use: boolean;
  /** May manage shared connections. */
  can_manage: boolean;
  /** May switch a tool on or off for the whole company (aai_enable_tools_company). */
  can_switch_company?: boolean;
  categories: { id: string; label: string }[];
  connectors: ConnectorRow[];
}

export interface ConnectorStatus {
  provider: string;
  scope: ConnectorScope;
  available: boolean;
  connected: boolean;
  status: string | null;
  label?: string | null;
}

// ── Routines (F1) ───────────────────────────────────────────────────────────
// A skill that runs on its own. `source` on a run says whether a person started
// it or the schedule did: both are real usage, but only a person's run counts
// toward a skill graduating.

export interface AaiRoutineRun {
  outcome: string;              // worked | partial | failed
  note: string | null;
  ran_at: string | null;
  source: 'manual' | 'routine';
}

export interface AaiRoutine {
  id: string;
  skill_id: string;
  skill_name: string;
  owner_email: string;
  cadence: 'daily' | 'weekdays' | 'weekly' | string;
  time_of_day: string;          // 24-hour local time, e.g. "09:00"
  weekday: number | null;       // weekly only, 0 = Monday
  timezone: string;
  paused: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  runs: AaiRoutineRun[];
}

// ── Employee portal: Results, Quick Wins, Activity ──────────────────────────
// The evidence surfaces behind the employee's Results and Quick Wins screens.
// `hours` and `usd` are nullable on purpose: null means nothing has been
// measured, which is a different statement from zero and has to stay one.

export interface TimeReturned {
  hours: number | null;
  reviewed_runs: number;
}

export interface RunCost {
  usd: number | null;
  priced_runs: number;
}

export interface ResultsLive {
  time_returned: TimeReturned;
  meaningful_use: number;
  needs_review: number;
  cost: RunCost;
}

export interface TrendMonth {
  label: string;
  hours: number;
}

export interface ResultDriver {
  name: string;
  hours: number;
}

export interface ExpectedValueRow {
  title: string;
  value_band: string | null;
  cost_band: string | null;
  status: string;
  id?: string;
  created_at?: string | null;
}

export interface PendingReview {
  execution_id: string;
  skill_id: string;
  skill_name: string;
  ran_at: string | null;
}

export interface MyResults {
  live: ResultsLive;
  trend: TrendMonth[];
  drivers: ResultDriver[];
  expected: ExpectedValueRow[];
  awaiting_review: PendingReview[];
}

/** A skill presented as a Quick Win: how far it reaches and what setup it needs. */
export interface QuickWin extends AaiSkill {
  scope_label: string;      // Personal | Department | Company
  audience_label: string;   // Everyone | a department | Your work
  setup_label: string;      // No setup needed | Already approved | ...
  form_label: string;       // Reusable | Run now
  ready: boolean;
}

export interface QuickWinList {
  items: QuickWin[];
  available: number;
  ready: number;
}

export type ActivityCategory =
  | 'projects' | 'orbit' | 'quick_wins' | 'adoption' | 'context' | 'results' | 'access' | 'billing' | 'workspace';

export interface ActivityEvent {
  id: string;
  action: string;
  label: string;
  category: ActivityCategory;
  actor_email: string | null;
  actor_role: string | null;
  target_email: string | null;
  department: string | null;
  trust: 'trusted' | 'record_only' | string;
  mine: boolean;
  entity_type: string | null;
  entity_id: string | null;
  entity_title: string | null;
  change: { from: unknown; to: unknown } | null;
  detail: Record<string, unknown>;
  created_at: string | null;
}

export interface ActivityFilters {
  category?: ActivityCategory | 'all';
  q?: string;
  actor?: string;
  since?: string;
  until?: string;
}

// The caller's own access to one client, one concept per field. A null is a
// fact the platform does not record, and the interface says so.
export interface AccessMine {
  role: string;
  assignment: string | null;
  valid_until: string | null;
  project_access: { count: number; basis: string };
  workspace_access: { name: string | null; basis: string };
  source_access: { count: number; basis: string };
}

export interface ActivityFeed {
  scope: string;
  requested_scope: string;
  events: ActivityEvent[];
}

// ── Employee settings ───────────────────────────────────────────────────────
// A person's own preferences, and the organisation boundary they sit inside.
// The boundary fields are nullable because some enterprise controls (session
// length, retention, data region) are not recorded by the platform yet: null
// means "no answer", which the interface must show rather than paper over.

export interface MemberNotifyPrefs {
  email_copies: boolean;
}

export interface MemberPreferences {
  start_page: string;
  density: string;
  language: string;
  timezone: string;
  notify: MemberNotifyPrefs;
}

export interface MemberBoundary {
  managed_by: string;
  security: {
    run_privacy: string;
    source_access: string;
    identity: string;
    session_policy: string | null;
    run_retention: string | null;
    data_region: string | null;
    external_sharing: string | null;
  };
  policy: { body: string; updated_at: string | null };
  ai: {
    policy: string;
    cleared_tools: number;
    routing: string | null;
    release: string | null;
  };
}

export interface MyPreferencesResponse {
  preferences: MemberPreferences;
  boundary: MemberBoundary;
}

// ── Orbit Control: the collection a Solution Partner prepares ───────────────

export interface OrbitControls {
  mode: string;
  identity: string;
  data: string;
  follow_ups: string;
  retention_days: number;
  cadence: string;
  /** Free text: the client's own wording for when the collection closes. */
  close_date: string;
  privacy_contact: string;
}

export interface OrbitInformation {
  id: string;
  name: string;
  question: string;
  /** Which round asks it: direct | person | team | organization. Derived from level when set. */
  round: string;
  format: string;
  collect: string;
  audience: string;
  context: string;
  required: boolean;
  /** The dossier field this question fills (a.company_name, dc.tools_active, onb.q11_tools). Empty = not tracked. */
  field_key?: string;
  level?: 'company' | 'department' | 'person' | '';
  /** Department slug for department and department-employee items. */
  scope?: string;
}

export interface OrbitGroupChoice {
  key: string;
  channel: string;
  /** Why Orbit needs to hear from them, when the proposal said. */
  reason?: string;
}

export interface OrbitRecipients {
  participation: string[];
  groups: OrbitGroupChoice[];
  /** What a scheduled Orbit call is booked for. Authored, so it is stored. */
  call_window?: { duration_min: number };
}

/** One probe answer put through the collection's controls. */
export interface OrbitProbe {
  kind: string;
  label: string;
  holds: boolean;
  action: string;
  risk: string;
}

export interface OrbitExampleRun {
  ran_at?: string;
  /** The version it ran against. A later edit makes the run stale, not wrong. */
  version?: number;
  /** Set when the rules or questions changed after the run on the same version. */
  stale?: boolean;
  passed?: boolean;
  reachable?: boolean;
  probes?: OrbitProbe[];
  ran_by?: string;
}

export interface OrbitCollection {
  id: string;
  client_id: string;
  created_by_email: string;
  version: number;
  status: 'draft' | 'sent' | 'approved' | 'changes_requested' | 'closed';
  title: string;
  goal: string;
  starting_point: string;
  owner_role: string;
  owner_email: string | null;
  controls: OrbitControls;
  information: OrbitInformation[];
  recipients: OrbitRecipients;
  context: string[];
  example_run: OrbitExampleRun;
  sent_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** The client owner's last decision on this version. */
  decision_note?: string | null;
  decided_by?: string | null;
  decided_at?: string | null;
  /** Source keys the owner confirmed on release. Empty until released. */
  approved_sources?: string[];
  closed_reason?: string | null;
  /** Present on list rows only: the readiness numbers a card shows, computed live. */
  summary?: OrbitCollectionSummary;
}

export interface OrbitCollectionSummary {
  passed: number;
  total: number;
  ready: boolean;
  reachable: number;
  groups: number;
  questions: number;
  next: { key: string; label: string; unmet: string };
}

/** What Orbit proposes from the goal. Nothing in it is saved until applied. */
export interface OrbitProposal {
  title: string;
  information: OrbitInformation[];
  groups: OrbitGroupChoice[];
  context: string[];
}

export interface OrbitProposalResponse {
  proposal: OrbitProposal;
  /** 'model' when Orbit proposed the plan, 'seed' when it could not be reached. */
  source: 'model' | 'seed';
}

/** A group the collection can reach. `eligible` is read from the live roster. */
export interface OrbitGroup {
  key: string;
  label: string;
  round: string;
  source: string;
  eligible: number;
  channel?: string;
  /** named = one accountable person (owner, a department head); cohort = a group the privacy threshold applies to. */
  kind?: 'named' | 'cohort';
  /** Department slug for head groups. */
  scope?: string;
}

export interface OrbitSource {
  key: string;
  label: string;
  sub: string;
  owner: string;
  data: string;
  expiry: string;
  available: boolean;
  state: string;
  detail: string;
}

export interface OrbitCheck {
  key: string;
  label: string;
  passed: boolean;
  detail: string;
  /** What to do about it. Empty when the check passes. */
  unmet: string;
}

export interface OrbitReadiness {
  checks: OrbitCheck[];
  passed: number;
  total: number;
  ready: boolean;
  reachable: number;
  groups: OrbitGroup[];
  sources: OrbitSource[];
  /** The platform's reporting threshold. One source of truth, read here, never hardcoded. */
  min_group_size: number;
}

/** One fact Orbit could know at a level, and whether it does. */
export interface OrbitGapItem {
  field_key: string;
  label: string;
  kind: string;
  why: string;
  state: 'known' | 'confirm' | 'missing';
  who_key: string;
  who_label: string;
  /** The role the field was written for (cto, cfo...). Empty when anyone at the level can answer. */
  role_hint: string;
  /** Person levels only: how many in the group have answered. Absent when suppressed. */
  total?: number;
  answered?: number;
  suppressed?: boolean;
}

export interface OrbitGapLevel {
  level: 'company' | 'department' | 'person';
  scope: string;
  label: string;
  audience_key: string;
  audience_label: string;
  items: OrbitGapItem[];
  known: number;
  confirm: number;
  missing: number;
  head_email?: string;
  members_key?: string;
  members?: number;
  suppressed?: boolean;
}

export interface OrbitGaps {
  levels: OrbitGapLevel[];
  known: number;
  confirm: number;
  missing: number;
  min_group_size: number;
  unavailable?: boolean;
}

// ── Orbit Room ───────────────────────────────────────────────────────────────
// What Orbit knows (Collected) and what it still needs (New to Ask), per bucket,
// scoped to the viewer's role. Nothing here is a plan; it is read from the
// dossier stores and the live queue of asks.

export type OrbitRoomPurpose = 'business' | 'department' | 'problem' | 'person';
export type OrbitAskStatus = 'open' | 'delegated' | 'asked' | 'answered' | 'skipped' | 'dropped';
export type OrbitAskKind = 'say' | 'choose' | 'choose_many' | 'number' | 'people';

export interface OrbitRoomFact {
  field_key: string;
  label: string;
  value: unknown;
  value_display: string;
  state: 'known' | 'confirm';
  source: string;
  confidence: number;
  by: string;
  when: string;
}

export interface OrbitAskHandoff { from: string; to?: string; when: string; note: string; }

export interface OrbitAsk {
  id: string;
  client_id: string;
  purpose: OrbitRoomPurpose;
  bucket: string;
  level: 'company' | 'department' | 'person';
  scope: string;
  field_key: string;
  question: string;
  why: string;
  kind: OrbitAskKind;
  options: { value: string; label: string }[];
  responsible_email: string;
  responsible_role: string;
  origin: string;
  /** Where this sits in the company's opening questions; 999 when it is not one. */
  opening_rank?: number;
  created_by: string;
  chain: OrbitAskHandoff[];
  status: OrbitAskStatus;
  later: boolean;
  /** On Your part: the intake this card belongs to, worded as the deck tags it. */
  intake_label?: string;
  onboarding: boolean;
  answer: { value: unknown; as_partner: boolean } | null;
  answered_by: string | null;
  answered_at: string | null;
  asked_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface OrbitRoomCounts { known: number; confirm: number; ask: number; waiting: number; people?: number; onboarded?: number; }

export interface OrbitRoomDepartment {
  id: string;
  name: string;
  slug: string;
  lead_email: string;
  head_name?: string;
  members: number;
  onboarded: number;
  fields_total?: number;
  counts?: OrbitRoomCounts;
}

export interface OrbitRoomPerson { email: string; name: string; role: string; onboarded: boolean; department?: string; department_name?: string; }

export interface OrbitRoomBucket {
  key: string;
  label: string;
  section: string;
  facts: OrbitRoomFact[];
  asks: OrbitAsk[];
  counts: OrbitRoomCounts;
  departments?: OrbitRoomDepartment[];
  department?: OrbitRoomDepartment;
  people?: OrbitRoomPerson[];
}

export interface OrbitRoomView {
  purpose: OrbitRoomPurpose;
  scope: string;
  buckets: OrbitRoomBucket[];
  departments: OrbitRoomDepartment[];
  sources: OrbitSource[];
  viewer: { email: string; role: string; full: boolean; department: string; department_name: string };
  /** The company as a COUNT, for a viewer whose own lens is one department.
      Null for everybody else: a full lens already reads the company itself. */
  company_summary: OrbitRoomSummary | null;
  unavailable: boolean;
}

export interface GrantsView {
  company_id: string;
  permissions: { id: string; name: string; description: string }[];
  roles: {
    value: string;
    label: string;
    rank: number;
    default: string[];
    overrides: Record<string, boolean>;
    effective: string[];
    manageable: boolean;
  }[];
  can_manage: boolean;
  /** The caller's authority rank; a role with a higher rank sits below them. */
  actor_rank?: number;
  /** Grants the caller holds, or null for platform staff who hold everything. */
  actor_grants: string[] | null;
}

export interface OrbitRoomSummary { known: number; confirm: number; open: number; onboarding: number; waiting: number; }

export interface OrbitOption {
  key: string;
  mark: string;
  label: string;
  desc: string;
}

export interface OrbitCollectionView {
  collection: OrbitCollection;
  groups: OrbitGroup[];
  sources: OrbitSource[];
  participation: OrbitOption[];
  starting_points: OrbitOption[];
  readiness: OrbitReadiness;
  /** Files already in the client's dossier. Company context, not a source to select. */
  documents?: number;
  /** Known versus missing context per level, computed when the view is read. */
  gaps?: OrbitGaps;
  run?: OrbitExampleRun;
}

export interface OrbitCollectionEvent {
  id: string;
  action: string;
  title: string;
  detail: string;
  actor_email: string;
  actor_label: string;
  created_at: string | null;
}

// ── Delivery Project panel ──────────────────────────────────────────────────

export interface DeliveryProject {
  id: string;
  client_id: string;
  requester_email: string;
  department: string | null;
  title: string;
  detail: string | null;
  risk: string | null;
  status: string;
  state_label: string;
  stage_chip: string;
  /** The StatusChip state for stage_chip, from the same table as the list rows. */
  status_chip?: string;
  next_action: string;
  reviewer_email?: string | null;
  reviewer_gate?: 'department' | 'company' | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string | null;
  model: string | null;
  human_in_loop: string | null;
  capability_tag: string | null;
  target_phase: string | null;
  scope_notes: string | null;
  owner: string | null;
  owner_named: boolean;
  /** The person this work was given to, if anyone. Not the same as `owner`,
      which falls back to whoever decided it. */
  assigned_expert_email?: string | null;
  problem_id?: string | null;
  problem_stage?: string | null;
  /** The intake recommendation, not the delivered kind. */
  route?: string | null;
  /** The shape Orbit recommended, and the sentence it gave for it. */
  deliverable?: 'skill' | 'plugin' | 'document' | 'routine' | null;
  deliverable_reason?: string | null;
  /** Who is on the work. Empty once decided means nobody has been named. */
  scopers?: RequestAssignee[];
  builders?: RequestAssignee[];
  /** The Workspace project this problem became, once a build started. */
  builder_project_id?: string | null;
  build_state?: 'built' | 'tested' | null;
  build_state_at?: string | null;
  build_state_source?: string | null;
  tool_skill_id?: string | null;
}

/** The published skill that answers the request, once one has shipped. */
export interface DeliveredTool {
  id: string;
  name: string;
  published: boolean;
  created_at: string | null;
}

/** How many other needs look like this one, and how that was decided. */
export interface DeliveryRelated {
  count: number;
  teams: number;
  basis: 'capability' | 'department' | 'unavailable';
}

/** `people` is null when the group is under the reporting threshold. */
export interface DeliveryAudience {
  scope: string;
  people: number | null;
  suppressed: boolean;
}

export interface DeliveryPerson {
  role: string;
  email: string;
  affiliation: string;
}

/** `value` is null when nothing was recorded; `unset` says so. */
export interface DeliveryControl {
  label: string;
  value: string | null;
  unset: string;
}

export interface DeliveryQuote {
  id: string;
  request_id: string;
  version: number;
  state: 'draft' | 'sent' | 'accepted' | 'superseded' | 'declined';
  price_amount: number | null;
  price_currency: string;
  delivery_window: string;
  confidence: 'Low' | 'Medium' | 'High';
  owner_name: string;
  expected_value: string;
  milestones: string[];
  assumptions: string[];
  exclusions: string[];
  valid_until: string;
  accepted_at: string | null;
  created_by_email: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface DeliveryNote {
  id: string;
  body: string;
  kind: 'record_only' | 'client_visible';
  author_email: string;
  author_label: string;
  created_at: string | null;
}

export interface DeliveryProjectPanel {
  project: DeliveryProject;
  related: DeliveryRelated;
  audience: DeliveryAudience;
  people: DeliveryPerson[];
  controls: DeliveryControl[];
  quotes: DeliveryQuote[];
  /** The version delivery works to: accepted if there is one, else newest. */
  quote: DeliveryQuote | null;
  tool_skill: DeliveredTool | null;
  notes: DeliveryNote[];
  min_group_size: number;
}

/** Settings > Plan & usage: the client's plan and their quote pipeline. */
export interface PlanUsageBucket {
  key: string;
  label: string;
  count: number;
}

/** One allowance the plan sets, counted from the records the guard counts. */
export interface PlanUsageMeter {
  key: string;
  label: string;
  value: number;
  /** Null when the plan sets no ceiling; `limit_label` then says so. */
  limit: number | null;
  limit_label: string | null;
  evidence: string;
  /** False when nothing records this yet, so the screen says so instead of showing a zero. */
  measured?: boolean;
  over: boolean;
}

/** One count of what the program did in the period. No plan ceiling applies. */
export interface PlanUsageCount {
  key: string;
  label: string;
  value: number;
  evidence: string;
}

export interface PlanUsagePeriod {
  start: string | null;
  end: string | null;
  source: 'subscription' | 'calendar_month';
  label: string;
}

export interface PlanUsageAiWork {
  turns: number;
  tokens: number;
  cost_usd: number | null;
  currency: string;
  evidence: string;
}

/** Renewal facts from the subscription itself. Null when none is recorded. */
export interface PlanUsageSubscription {
  status: string | null;
  billing_period: string | null;
  renews_at: string | null;
  cancel_at_period_end: boolean;
}

export interface PlanUsageMetering {
  period: PlanUsagePeriod;
  subscription: PlanUsageSubscription | null;
  plan_meters: PlanUsageMeter[];
  program_meters: PlanUsageCount[];
  ai_work: PlanUsageAiWork;
}

export interface PlanAndUsageData {
  plan: { display_name: string | null; seats: number | null };
  seats_used: number;
  pipeline: PlanUsageBucket[];
  /** Null when nothing is accepted; `mixed_currencies` when a total is meaningless. */
  accepted_value: {
    mixed_currencies: boolean;
    amount: number | null;
    currency: string | null;
    quotes: number;
  } | null;
  usage: PlanUsageMetering;
}

// ── Solution Partner settings: the partner's own surface on one assignment ──

export interface PartnerSession {
  id: string;
  user_agent: string;
  ip: string | null;
  created_at: string | null;
  last_seen_at: string | null;
}

export interface PartnerAccount {
  display_name: string | null;
  email: string;
  team: string | null;
  established_role: string;
  sign_in_method: string;
  sessions: PartnerSession[];
}

/** `value` null means nothing is recorded; `unset` says so. */
export interface PartnerStatement {
  kicker: string;
  label: string;
  value: string | null;
  unset?: string;
  enforced: boolean;
}

export interface PartnerSecurity {
  boundary_name: string;
  statements: PartnerStatement[];
  policies: { label: string; value: string | null }[];
  managed_by: string;
}

export interface PartnerAppEntry {
  mark: string;
  label: string;
  sub: string;
  owner: string;
  owner_sub: string;
  count: number | null;
  state: string;
  detail: string;
}

export interface PartnerApps {
  entries: PartnerAppEntry[];
  personal_requests_allowed: boolean;
  managed_by: string;
}

export interface PartnerSettingsData {
  account: PartnerAccount;
  security: PartnerSecurity;
  apps: PartnerApps;
}

// ── Solution Partner overview (every assigned client in one read) ───────────
// The lifecycle status is derived server-side from recorded facts: an intake
// link issued, that link opened, and Layer 1 of the dossier complete.
export type PartnerClientStatus = 'draft' | 'invited' | 'onboarding' | 'active';

export type PartnerAttentionKind = 'finish_setup' | 'invite_not_sent' | 'invite_not_opened' | 'project_waiting' | 'opportunities_to_review';

export interface PartnerAttention {
  kind: PartnerAttentionKind;
  label: string;
  detail: string;
  request_id?: string;
}

export interface PartnerClientRow {
  id: string;
  name: string;
  initials: string;
  contact_name: string | null;
  contact_email: string | null;
  website: string | null;
  phone: string | null;
  /** The provider pays for this client's projects under a retainer agreement. */
  retainer?: boolean;
  created_at: string | null;
  status: PartnerClientStatus;
  onboarding_pct: number;
  onboarding_captured: number;
  onboarding_total: number;
  onboarding_label: string;
  orbit: { confidence: number; thin: string[]; ready: boolean } | null;
  documents: number;
  projects_open: number;
  opportunities_to_review: number;
  opportunities_awaiting_client: number;
  invite_issued_at: string | null;
  last_activity: { label: string; at: string | null } | null;
  next_up: { title: string; at: string | null } | null;
  attention: PartnerAttention | null;
}

// ── Opportunities: a problem card along its review axis ─────────────────────
export type OpportunityReviewState =
  | 'draft' | 'needs_review' | 'ready_for_client' | 'awaiting_client' | 'confirmed' | 'declined' | 'routed';

export interface AaiOpportunity {
  id: string;
  engagement_id: string | null;
  status: string;                 // Orbit scoping stage: DRAFT | SCOPED | PRD_READY
  stage: string | null;           // the stage as the partner reads it
  title: string;
  statement: string | null;
  target: string | null;
  clarity_score: number | null;
  verdict: string | null;
  seed_source: string | null;
  review_state: OpportunityReviewState;
  partner_summary: string | null;
  partner_notes: string | null;
  origin: Record<string, unknown>;
  client_decided_by: string | null;
  client_decided_at: string | null;
  client_decision_note: string | null;
  created_at: string | null;
  updated_at: string | null;
  request_id: string | null;
  request_status: string | null;
  /** A request-origin row's own words, the same as its row on Projects. */
  request_display?: RequestDisplay;
  /** Who asked for it. Null on an Opportunity Orbit found in the dossier. */
  requester_email?: string | null;
  /** What shape it would be built as. Null means nobody classified it. */
  deliverable?: 'skill' | 'plugin' | 'document' | 'routine' | null;
  route?: 'quick_win' | 'project' | null;
  /** Where it came from: Orbit reading the dossier, or a person asking. */
  source_kind?: 'problem' | 'request';
}

export interface PartnerAttentionItem extends PartnerAttention {
  client_id: string;
  client_name: string;
  status: PartnerClientStatus;
}

// A summary line per client: how much changed and whether a decision waits.
// Never the events themselves; those are read inside the client.
export interface PartnerUpdateItem {
  client_id: string;
  client_name: string;
  status: PartnerClientStatus;
  count: number;
  latest_label: string | null;
  latest_at: string | null;
  waiting: number;
}

export interface PartnerScheduleItem {
  id: string;
  client_id: string;
  client_name: string;
  title: string;
  kind: string;
  at: string | null;
}

export interface PartnerOverview {
  clients: PartnerClientRow[];
  counts: { active: number; draft: number; onboarding: number; attention: number };
  attention: PartnerAttentionItem[];
  updates: PartnerUpdateItem[];
  schedule: PartnerScheduleItem[];
}

// ── Owner overview: decisions first, then counts, work and connections ──────
// One composed read (GET /aai/owner/overview). Every element points at the
// record it concerns; the Owner acts on the record, never on this payload.

export type OwnerDecisionKind = 'opportunity' | 'request' | 'quote';

export interface OwnerDecision {
  kind: OwnerDecisionKind;
  id: string;
  request_id: string | null;
  title: string;
  detail: string | null;
  department: string | null;
  since: string | null;
  price_amount?: number | null;
  price_currency?: string;
  delivery_window?: string;
}

export interface OwnerConnection {
  provider: string;
  status: string;
  sync_status: string | null;
  error_message: string | null;
  last_sync_at: string | null;
  owner_email: string | null;
  updated_at: string | null;
  /** True when the state needs a business decision rather than an engineer. */
  attention: boolean;
}

export interface OwnerOverview {
  decisions: OwnerDecision[];
  counts: {
    opportunities: number;
    opportunities_awaiting: number;
    quick_wins: number;
    projects: number;
    /** Open requests carrying a high risk band. */
    material_risk: number;
    decisions: number;
    /** Every open request, the number the Requests tab shows. */
    active_work?: number;
  };
  /** Client-visible Opportunities only: awaiting_client, confirmed, declined, routed. */
  opportunities: AaiOpportunity[];
  work: AaiRequest[];
  connections: OwnerConnection[];
  connections_attention: OwnerConnection[];
  plan: PlanAndUsageData;
}

/** GET /aai/owner/ai-control: what the company's AI runs on. Read-only for the Owner. */
export interface AiControl {
  managed: { status: 'active' | 'direct'; routes: { alias: string; lane: string; fallback: string; status: string }[] };
  providers: { key: string; label: string; detail: string; owner: string; status: string }[];
  registry: {
    current_model: string | null;
    base_model: string | null;
    last_trained_at: string | null;
    training_examples_count: number;
    status: string;
  } | null;
  use_case_models: { model: string | null; human_in_loop: string | null; count: number }[];
  policy: { present: boolean; body: string; updated_at: string | null };
  cleared_tools: number;
}

// ── Controls: derived state plus the Owner's recorded control actions ─────────

export type ControlEventKind =
  | 'access_review' | 'scope_change_request' | 'disconnect_request'
  | 'incident_drill' | 'emergency_review' | 'evaluation_request' | 'posture_verification'
  | 'capability_feedback' | 'customization_request'
  | 'program_review' | 'session_exception' | 'evidence_review'
  | 'support_request' | 'blocker_assignment' | 'demonstration';

export interface ControlEvent {
  id: string;
  kind: ControlEventKind;
  subject: string;
  status: string;
  actor_email: string | null;
  payload: Record<string, unknown>;
  created_at: string | null;
  /** Present on the platform-wide blocker list only. */
  company_id?: string;
  company_name?: string;
}

export interface ControlConnection extends OwnerConnection {
  scopes: string[];
  account_name: string | null;
  connected_at: string | null;
  access_review: ControlEvent | null;
  scope_change_request: ControlEvent | null;
  disconnect_request: ControlEvent | null;
}

export interface PostureItem {
  id: string;
  title: string;
  desc: string;
  kind: 'runtime' | 'policy';
  evidence: string;
  fail_safe: string;
  state: 'verified' | 'policy_ready' | 'verification_due';
  owner: string;
  last_verified_at: string | null;
}

export interface IncidentItem {
  id: 'containment_drill' | 'emergency_stop';
  kind: 'incident_drill' | 'emergency_review';
  title: string;
  desc: string;
  state: 'runbook_ready' | 'drill_started' | 'reviewed' | 'review_due';
  last_event: ControlEvent | null;
  covers?: string;
  evidence_required?: string;
  business_content?: string;
  scenarios?: string[];
  boundaries?: { label: string; value: string }[];
  results?: string[];
}

export interface OwnerControls {
  counts: { access_reviews: number; capability_candidates: number; release_checks: number };
  access_alert: ControlConnection | null;
  connections: ControlConnection[];
  posture: PostureItem[];
  incident: IncidentItem[];
  release: { approved: number; ready: number; latest: { id: string; title: string; department: string | null } | null };
  ai: AiControl;
  events: ControlEvent[];
}

/** GET /aai/connectors/organization: shared connections plus personal coverage as counts. */
export interface OrganizationConnections {
  organization: { provider: string; status: string }[];
  personal_coverage: { provider: string; members_connected: number }[];
  disabled: string[];
}

// ── Owner adoption controls ─────────────────────────────────────────────────
export interface OwnerProgram {
  name: string;
  enabled: boolean;
  preset: string | null;
  tier: string | null;
  tier_label: string;
  starts_on: string | null;
  managed_by: 'trainer' | 'champion' | string;
  manager_label: string;
  handed_off_at: string | null;
  updated_at: string | null;
  created_at: string | null;
  phases: number;
}
export interface OwnerLatestEvent {
  id: string;
  status: string;
  actor_email: string | null;
  payload: Record<string, unknown>;
  created_at: string | null;
}
export interface SessionException {
  id: string;
  title: string;
  session_type: string;
  scheduled_at: string | null;
  duration_min: number | null;
  cohort: string | null;
  exception: string;
  overdue: boolean;
  request: OwnerLatestEvent | null;
}
export interface GraduationProposal {
  id: string;
  skill: string;
  from_scope: string;
  to_scope: string;
  department: string | null;
  proposed_by: string | null;
  created_at: string | null;
  note: string | null;
  runs: number;
  worked: number;
  review: OwnerLatestEvent | null;
}
export interface OwnerAdoption {
  program: OwnerProgram | null;
  program_review: OwnerLatestEvent | null;
  review_due: boolean;
  next_review: string | null;
  managers: string[];
  exceptions: SessionException[];
  cohorts: { id: string; name: string; members: number }[];
  proposals: GraduationProposal[];
  counts: { program_review: number; exceptions: number; evidence_reviews: number };
}

// ── The Department Head's composed read (GET /api/aai/department/overview) ─────

export interface DepartmentIdentity {
  name: string | null;
  id: string | null;
  lead_email: string | null;
  /** The caller is the recorded lead of this department, which is what gate-1
   *  approval checks. Holding the role without being the lead reads but cannot
   *  decide, and the UI says so. */
  is_lead: boolean;
  managed: string[];
}

export interface DepartmentDecision {
  kind: 'request';
  id: string;
  request_id: string;
  title: string;
  detail: string | null;
  department: string | null;
  since: string | null;
  status: string;
  /** What Orbit recommended at intake. Null where nothing was routed. */
  route?: 'quick_win' | 'project' | null;
  /** And what shape it would be built as. Null where nothing was classified. */
  deliverable?: 'skill' | 'plugin' | 'document' | 'routine' | null;
  est_value_band: string | null;
  can_decide: boolean;
}

export interface DepartmentWork extends AaiRequest {
  your_part: 'owner' | 'department_head' | 'viewer';
  /** No membership record exists yet, so Workspace entry is never asserted. */
  workspace: 'not_recorded';
}

export interface DepartmentQuickWin {
  id: string;
  name: string;
  problem: string | null;
  scope: string;
  department: string | null;
  access_level: string;
  created_by: string | null;
  use_case_id: string | null;
  use_case_title: string | null;
  /** Share of the department using it; null under the reporting threshold. */
  usage_pct: number | null;
}

export interface DepartmentPattern {
  key: string;
  label: string;
  count: number;
  departments: string[];
  /** The caller's own department's requests behind the pattern. Other
   *  departments contribute to the count only. */
  requests: { id: string; title: string; status: string }[];
}

export interface DepartmentReadiness {
  people: number;
  onboarded: number;
  suppressed: boolean;
  suppressed_reason?: string | null;
  ready: number | null;
  learning: number | null;
  need_support: number | null;
  stage: string | null;
  maturity: MaturityScore;
  trend: DashboardTrend;
}

export interface DepartmentResultDriver {
  id: string;
  name: string;
  scope: string;
  department: string | null;
  minutes: number;
  reviews: number;
}

export interface DepartmentExpected {
  id: string;
  title: string;
  est_value_band: string | null;
  status: string;
}

export interface DepartmentResults {
  suppressed: boolean;
  suppressed_reason?: string;
  confirmed_minutes?: number;
  confirmed_hours?: number;
  reviews?: number;
  worked?: number;
  meaningful_use_pct?: number;
  needs_review?: number;
  needs_review_pct?: number | null;
  cost_usd?: number;
  trend?: { month: string; minutes: number; reviews: number }[];
  drivers?: DepartmentResultDriver[];
  expected: DepartmentExpected[];
}

export interface DepartmentOverview {
  department: DepartmentIdentity;
  counts: { decisions: number; work: number; projects: number; quick_wins: number; need_support: number | null };
  decisions: DepartmentDecision[];
  work: DepartmentWork[];
  quick_wins: DepartmentQuickWin[];
  patterns: DepartmentPattern[];
  readiness: DepartmentReadiness | null;
  results: DepartmentResults | null;
  support_requests: ControlEvent[];
  /** Support and demonstrations for this department, whoever raised them. */
  actions: ControlEvent[];
  access: { scope: string | null; source_grants: number; connections: OwnerConnection[] };
  min_group_size: number;
}

// ── Portfolio recommendation: the AI Champion's portfolio-level ask of the Owner ──
// Snapshots taken at send time; the Project records keep their live values.

export type PortfolioRecommendationStatus = 'sent' | 'accepted' | 'adjusted' | 'declined' | 'superseded';

export interface PortfolioRecommendationItem {
  request_id: string;
  rank: number;
  title: string;
  department: string | null;
  kind: 'Quick Win' | 'Delivery Project';
  status: string | null;
  risk: string | null;
  forecast_value_band: string | null;
  setup_band: string | null;
}

export interface BandSum {
  low: number | null;
  high: number | null;
  counted: number;
  without: number;
}

export interface PortfolioRecommendation {
  id: string;
  company_id: string;
  period: string;
  version: number;
  status: PortfolioRecommendationStatus;
  items: PortfolioRecommendationItem[];
  forecast_value: BandSum;
  setup_range: BandSum;
  recommended_limit: number | null;
  currency: string;
  note: string | null;
  sent_by: string;
  sent_to: string;
  sent_at: string | null;
  responded_by: string | null;
  responded_at: string | null;
  final_limit: number | null;
  response_note: string | null;
  superseded_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PortfolioRecommendationList {
  recommendations: PortfolioRecommendation[];
  executives: string[];
}

/* ── the deck, the overview, the framework ──────────────────────────────── */

export interface OrbitProposal {
  id: string;
  kind: 'department' | 'person';
  name: string;
  title: string;
  department: string;
  email: string;
  tools: string[];
  source: string;
  status: string;
  decided_by: string;
  created_at: string | null;
}

export interface OrbitDeckCard {
  id: string;
  kind: 'confirm' | 'confirm_org' | 'ask' | 'evidence';
  tag: 'confirm' | 'ask' | 'evidence';
  tag_label: string;
  title: string;
  why: string;
  facts?: OrbitRoomFact[];
  scope?: string;
  proposals?: OrbitProposal[];
  ask?: OrbitAsk;
  input?: OrbitAskKind | 'evidence';
  options?: { value: string; label: string; detected?: boolean; source?: string }[];
  handed_by?: string | null;
  rank?: number;
  onboarding?: boolean;
  /** Set on a card of the viewer's own person intake: answered through My part. */
  person_key?: string;
  /** An answer Orbit already holds, filled in for the person to keep or change. */
  prefill?: string;
  /** Which intake a card after the opening six belongs to; each is its own deck. */
  intake?: 'business' | 'tool' | 'department' | 'person' | 'other';
}

export interface OrbitDeckResponse {
  cards: OrbitDeckCard[];
  /** The rest of this person's dossier, dealt only when they continue past the starter cards. */
  rest: OrbitDeckCard[];
  company_name: string;
  site: string;
  known: number;
  audience: 'me' | 'partner';
  counts: { skipped: number; left: number; handed: number };
  /** Set while the tools are not named yet: the questions each named tool adds to the Tool intake. */
  per_tool?: number;
  viewer: { email: string; role: string; department: string; department_name: string };
}

export interface OrbitOverviewStep { key: string; title: string; detail: string; state: 'done' | 'now' | 'next' | 'locked'; }
export interface OrbitOverviewArea { key: string; label: string; pct: number; verdict: string; known: number; confirm: number; ask: number; }
export type OrbitDossierKind = 'company' | 'department' | 'proposed_department' | 'person' | 'goals' | 'tools' | 'people'
  /* An intake the partner defined, one per company. */
  | 'intake';
export type OrbitDossierState = 'open' | 'waiting' | 'not_opened' | 'no_head' | 'confirm';

export interface OrbitOverviewDossier {
  key: string;
  kind: OrbitDossierKind;
  name: string;
  /** Which Room bucket holds this dossier's facts and asks. */
  bucket: string;
  head: string | null;
  head_email?: string;
  members?: number;
  onboarded?: number;
  heads?: number;
  known: number;
  confirm: number;
  ask: number;
  waiting: number;
  fields_total: number;
  pct: number;
  verdict: string;
  state: OrbitDossierState;
  detected: string;
  department?: OrbitRoomDepartment;
  proposal?: OrbitProposal;
}

export type OrbitPhaseState = 'done' | 'now' | 'locked';

export interface OrbitPhaseMark {
  n: number;
  key: string;
  screen?: PhaseScreen;
  title: string;
  purpose?: string;
  builds?: string[];
  unlocks?: string;
  action?: string;
  hidden?: boolean;
  state: OrbitPhaseState;
  started_at: string | null;
  started_by: string;
  done_at: string | null;
  done_by: string;
}

export interface OrbitJourney {
  done_max: number;
  open: number;
  phases: OrbitPhaseMark[];
  /** Which way Orbit is leaning on this client, and whether somebody chose it
      here or it came from the partner's own framework. */
  depth?: 'balanced' | 'tools' | 'departments';
  depth_label?: string;
  depth_line?: string;
  depth_set_here?: boolean;
}

export interface OrbitOverview {
  /** People added for the Person intake step, and how many have finished it. */
  person_intake?: { people: number; done: number };
  steps: OrbitOverviewStep[];
  todo: string[];
  confidence: number;
  /** Maps still too thin to rely on, weakest first. */
  thin?: { key: string; label: string; pct: number }[];
  sentence: string;
  /** Set for a department head: the counts are that department's. */
  department_name?: string;
  counts: { known: number; confirm: number; open: number; departments: number; people: number; onboarded: number };
  areas: OrbitOverviewArea[];
  dossiers: OrbitOverviewDossier[];
  /** Where the facts on file came from, as the Room words them. */
  sources: string[];
  opportunities: { locked: boolean; unlock_ask: OrbitAsk | null; open_count: number; items: OrbitOpportunityItem[] };
  proposals: OrbitProposal[];
}

export interface OrbitOpportunityItem {
  id: string;
  kind: 'opportunity' | 'request';
  title: string;
  summary: string;
  level: string;
  for: string;
  state: string;
  tone: 'rdy' | 'val' | 'off';
  review_state: OpportunityReviewState | '';
  value: string;
  cost: string;
  type: string;
  group: 'company' | 'person';
  department: string;
  request_id: string;
  request_status: string;
  stage: string;
  source: string;
  decided_by: string;
  link: string;
  requester?: string;
}

export interface FrameworkField {
  key: string;
  section: string;
  label: string;
  kind: string;
  options: { value: string; label: string }[] | null;
  placeholder: string | null;
  why: string | null;
  leverage: string;
  critical: boolean;
  required_for_routing: boolean;
  layer: string;
  provenance: 'default' | 'global' | 'consultant' | 'client';
  added: boolean;
  hidden: boolean;
  locked: boolean;
  has_override_here: boolean;
  visible_rule: Record<string, unknown> | null;
  ask: { orbit_voice?: string; required?: boolean } | null;
  starter_rank: number | null;
  starter_here: number | null;
  override_op?: string;
  override_scope?: string;
}

export interface FrameworkSectionOverride { hidden?: boolean; label?: string; visible_when?: Record<string, unknown>; scope: string; has_override_here: boolean; }

export interface FrameworkView {
  registry: string;
  client_id: string | null;
  scope: string;
  scope_kind: 'global' | 'consultant' | 'client';
  chain: string[];
  etag: string;
  sections: Record<string, string>;
  section_order: string[];
  section_overrides: Record<string, FrameworkSectionOverride>;
  hidden_sections: string[];
  /** Sections only the platform writes to; a partner cannot add a question there. */
  platform_only_sections?: string[];
  starter: { keys: string[]; scope: string; limit: number };
  kinds: string[];
  fields: FrameworkField[];
}

export interface PartnerFramework { consultant_id: string; name: string; email: string; overrides: number; by_op: Record<string, number>; custom: boolean; }

/* An intake a Solution Partner defined. `mine` is false on one inherited from
   the platform or a partner above this scope: visible here, edited there. */
export interface IntakeTypeRow {
  slug: string;
  scope: string;
  section: string;
  label: string;
  blurb: string;
  mine: boolean;
}

/* A question one document says the framework should ask. `spec` is what the
   save takes, `refused` is why the registry would not take it, and `covered_by`
   names the question already asking this. */
export interface FrameworkFieldProposal {
  key: string;
  label: string;
  kind: string;
  options: { value: string; label: string }[];
  why: string;
  evidence: string;
  spec: Record<string, unknown>;
  covered_by: string;
  covered_label: string;
  refused: string;
  /* How many of the documents in one read asked for this. Agreement between a
     partner's own files is the closest thing to a vote on what their method
     actually asks. */
  documents?: number;
  /* A whole-framework read files each question: the place it lands (target
     id), that place's question list, and which intake it is. */
  target?: string;
  registry?: string;
  intake?: string;
  /* Set on screen when the partner moves a question to another intake. */
  moved_from?: string;
  /* A whole-method read: the part of the partner's method it belongs to, in
     their own words, whether the method words it exactly this way (then Orbit
     asks it as written), and the document the wording came from. */
  part?: string;
  exact?: boolean;
  document?: string;
}

/* One part of a partner's method, in their own name: an audit, a stage. */
export interface FrameworkMethodPart {
  name: string;
  about: string;
}

/* A step of the method that is not a question Orbit can put to one person. */
export interface FrameworkMethodStep {
  name: string;
  what: string;
  part: string;
  type: 'exercise' | 're-check' | 'conversation' | 'scoring' | 'teaching' | 'other';
}

/* One place a question can land in a whole-framework read. */
export interface FrameworkReadTarget {
  id: string;
  intake: string;
  intake_label: string;
  registry: string;
  section: string;
  /** A section the partner added: its questions are stored under `section`. */
  group?: string;
  label: string;
}

/* An answer the same document already holds about one company. `answered` means
   somebody at that company has confirmed this field, so the writer will refuse
   to overwrite it and the row is shown as settled rather than offered. */
export interface FrameworkValueProposal {
  key: string;
  label: string;
  value: unknown;
  value_display: string;
  confidence: number;
  source_ref: string;
  answered: boolean;
}

/* How deep Orbit works on one scope. A hint, not a switch: no phase is skipped
   and Orbit still decides what to ask. `set_here` separates "this scope was
   given its own lean" from "it is following the one above", which read the same
   on screen and are different decisions underneath. */
export interface JourneyHint {
  depth: 'balanced' | 'tools' | 'departments';
  scope: string;
  from_scope: string;
  set_here: boolean;
  inherited: boolean;
  plan: { ask_flows: boolean; tool_ask_cap: number; suggest_dives: number };
  options: { value: string; label: string; line: string }[];
}

export interface FrameworkDocumentRead {
  filename: string;
  read: boolean;
  words: number;
  fields: FrameworkFieldProposal[];
  /* Why the list above is empty: "nothing new here" and "Orbit could not look"
     are different facts and the screen says which. Blank when it did look. */
  fields_note: string;
  values: FrameworkValueProposal[];
  values_note: string;
  reason: string;
  scope: string;
  /* The order of stages the documents run an engagement in, when they lay one
     out, as the standard journey rearranged and ready to apply. */
  journey?: FrameworkReadJourney;
  /* A whole-framework read: every place a question could land, and the keys of
     the questions the documents say to open with, in order. */
  mode?: 'framework';
  targets?: FrameworkReadTarget[];
  /* Intakes Orbit decided the documents need, each a place a question can go
     before it exists: `id` is `new:<n>`, used as the questions' target. */
  intakes?: { id: string; label: string; about: string; questions: number }[];
  opening?: string[];
  /* What the documents leave open about how to ask, as questions for the partner. */
  gaps?: { question: string; why: string }[];
  /* One row per file in the selection. A folder where two files held no
     readable text is a different answer from one where none did, and the
     screen says which without the partner opening anything. */
  documents?: FrameworkReadDocumentLine[];
  /* 2 is the whole folder read at once as one method. */
  version?: number;
  parts?: FrameworkMethodPart[];
  steps?: FrameworkMethodStep[];
}

export interface FrameworkReadJourney {
  found: boolean;
  /** Why there is no order, when there is none. */
  note?: string;
  stages?: string[];
  evidence?: string;
  filename?: string;
  phases?: ArcPhase[];
}

export interface FrameworkReadDocumentLine {
  filename: string;
  read: boolean;
  words: number;
  reason: string;
  /* Whether Orbit read it as the method itself or as background to it. */
  role?: 'method' | 'background' | '';
  why?: string;
}

/* A read that outlives the screen that started it. The upload returns one of
   these with nothing in `result`; the answer arrives in a later poll, and a
   partner who walked away finds it by asking for the newest job at this scope. */
export interface FrameworkReadJob {
  id: string;
  scope: string;
  client_id: string;
  registry: string;
  status: 'running' | 'done' | 'failed';
  total: number;
  finished: number;
  /** The file being read right now. Empty between files and once done. */
  step: string;
  filenames: string[];
  result: FrameworkDocumentRead | null;
  error: string;
  age_seconds: number;
}

/* A document the framework was built from, kept so it can be read again and
   taken back out. Before this the bytes were discarded after the read and only
   the `doc:<filename>` provenance string survived. */
export interface FrameworkStoredDocument {
  id: string;
  scope: string;
  registry: string;
  /* Which intake it was read into, which is not the registry: the business and
     the person intake are both the dossier, and a list that says "dossier"
     three times cannot be sorted by anybody. */
  intake: string;
  filename: string;
  content_type: string;
  bytes: number;
  read_job_id: string;
  created_at: string;
}

/* The person-first intake. Every other intake starts from a company and works
   down to its people; this one starts from one person, who has no account and
   may not work anywhere we have heard of. */
/* One Orbit conversation, as a thing with an owner and a start rather than the
   forty-message buffer a company used to share. This is the ticket. */
export interface OrbitConversation {
  id: string;
  person_email: string;
  purpose: 'business' | 'problem';
  problem_id: string;
  /** The journey phase it happened in, stamped when it opened and never moved. */
  phase: string;
  title: string;
  status: 'open' | 'closed';
  turns: number;
  opened_at: string;
  last_at: string;
  messages?: { role: string; content: string }[];
}

/* One phase of an engagement arc. The words travel with it now: a partner can
   write their own arc, so a screen cannot hold its own copy of the six. */
/* Which intake a journey step opens on the client's screen. `none` is a step
   with no intake of its own, such as a workshop or a sign-off. */
/* `intake:<slug>` is a step for an intake the partner defined, opening its cards. */
export type PhaseScreen = 'frame' | 'departments' | 'team' | 'tools' | 'report' | 'run' | 'none' | `intake:${string}`;

export interface ArcPhase {
  n: number;
  key: string;
  screen?: PhaseScreen;
  title: string;
  purpose: string;
  builds: string[];
  unlocks: string;
  action: string;
  hidden?: boolean;
}

export interface EngagementTemplate {
  id: string;
  consultant_id: string;
  name: string;
  blurb: string;
  phases: ArcPhase[];
  created_by: string;
  updated_at: string;
  /** Ours, offered so an admin starts from something rather than a blank page. */
  is_default: boolean;
}

/* Something a partner's documents leave open about how to ask, and their answer. */
export interface FrameworkGap {
  question: string;
  why: string;
  answer: string;
}

/* A partner's framework setup: their own or ours, and whether it is live. */
export interface FrameworkProfile {
  scope: string;
  choice: '' | 'ours' | 'own';
  status: '' | 'setting_up' | 'live';
  gaps: FrameworkGap[];
  updated_at: string;
  /** A client's screen shows its partner's setup and cannot change it. */
  is_client: boolean;
  editable: boolean;
  /** On the Agents Anywhere framework: its questions are ours and cannot be changed here. */
  locked?: boolean;
}

/* The order a partner set for all their clients. Null means they walk ours. */
export interface PartnerJourneyDefault {
  template_name: string;
  phases: ArcPhase[];
  updated_by: string;
  updated_at: string;
}

/* The arc a client is actually walking, frozen when it was applied. Null until
   somebody prepares them, and then they walk the default six. */
export interface AppliedEngagement {
  template_id: string;
  template_name: string;
  phases: ArcPhase[];
  prepared_by: string;
  prepared_at: string;
}

/** What the copilot drafted. Nothing is saved until the admin applies it. */
export interface ProposedArc {
  name: string;
  phases: ArcPhase[];
  generated: boolean;
  /** Why this is the standard journey rather than a draft, when it is. */
  reason: string;
}

export interface EngagementBoard {
  columns: {
    key: string;
    title: string;
    n: number;
    /** The Room's own phase state, so the board cannot disagree with the journey. */
    state: 'done' | 'now' | 'locked' | '';
    tickets: OrbitConversation[];
  }[];
  total: number;
  open: number;
  people: string[];
}

export interface PersonIntakeCard {
  key: string;
  label: string;
  kind: string;
  /* `subtitle` is what gives a closed scale its ends: on the AI Trust Audit's
     1-10 it is what makes 1 and 10 mean something rather than being two of ten
     bare numbers. */
  options: { value: string; label: string; subtitle?: string }[];
  why: string;
  placeholder: string;
}

/* A card of one person's intake as their partner sees it: what is answered,
   and whether they answered it or the partner did on their behalf. */
export interface ClientPersonCard extends PersonIntakeCard {
  value: unknown;
  answered: boolean;
  skipped: boolean;
  by: '' | 'partner' | 'person';
  to_confirm: boolean;
  /** Who filled it on their behalf, when somebody did. */
  filled_by?: string;
}

/* A person a partner added at one client for the Person intake step. */
export interface ClientPerson {
  id: string;
  name: string;
  email: string;
  job_title: string;
  role: string;
  /* not_started, under_way, answered (every card, no login yet), invited
     (in the team, not signed in), done (signed in). */
  stage: 'not_started' | 'under_way' | 'answered' | 'invited' | 'done';
  answered: number;
  total: number;
  now_on: string;
  to_confirm: number;
  invited_at: string;
  member_at: string;
  /** Why a finished person could not be added to the team, if they could not. */
  member_note: string;
  blocks: { section: string; title: string; cards: ClientPersonCard[] }[];
}

/* A person's own part, for a company whose partner is not the house partner. */
export interface MyPart {
  /** The company runs its own journey (house partner, or none): show the Journey instead. */
  house: boolean;
  kind: 'company' | 'department' | 'person';
  intake: ClientPerson | null;
  work: { id: string; title: string; status: string; link: string }[];
  department: { slug: string; name: string; members: number; onboarded: number; tools: string[] } | null;
  company: { departments: { slug: string; name: string }[]; tools: string[] } | null;
  /** Every card waiting on them, and what Orbit already holds for them to confirm. */
  cards?: {
    asks: OrbitAsk[];
    held: (OrbitRoomFact & { scope: string; group: string })[];
    /** The same cards as the journey's steps, in its order and under its names. */
    groups?: { key: string; title: string; purpose: string; n: number; done: boolean; next: boolean; locked?: boolean; asks: OrbitAsk[]; held: (OrbitRoomFact & { scope: string; group: string })[] }[];
  };
  /** Intakes the partner's framework does without: their parts of this screen go too. */
  intakes_off?: string[];
}

export interface PersonIntakeBlock {
  section: string;
  title: string;
  cards: PersonIntakeCard[];
}

export interface PersonIntakeQuestions {
  partner: { id: string; name: string } | null;
  blocks: PersonIntakeBlock[];
  error: string;
}

/* One question on the session walk. `question` is what the interviewer worded
   for this person; `label` is the registry's own wording, which is what they
   are shown when the model is unavailable. */
export interface PersonIntakeQuestion extends PersonIntakeCard {
  section: string;
  block_title: string;
  question: string;
  generated: boolean;
  /** Answered on their behalf by their partner: shown filled, to keep or change. */
  confirming?: boolean;
  prefill?: unknown;
  /** Who filled it in for them: their partner, or their company's Owner. */
  filled_by?: string;
}

export interface PersonIntakeState {
  session_id: string;
  stage: 'asking' | 'employer' | 'done';
  question: PersonIntakeQuestion | null;
  answered: number;
  /* Shrinks when a question turns out not to apply to this person, rather than
     the answered count creeping up for something they never did. */
  total: number;
  name: string;
  /** A person their partner added at a client: never asked for an employer. */
  client_bound?: boolean;
  /** Finishing added them to the team, so a sign-in email is on its way. */
  joined?: boolean;
}

export interface PersonIntakeLink {
  url: string;
  token: string;
  expires_at: string;
  expires_days: number;
}

/* One dossier section this person is placed to answer, and the answer of their
   own that placed them there. */
export interface SpeaksTo {
  section: string;
  label: string;
  why: string;
}

export interface PersonProspect {
  id: string;
  name: string;
  email: string;
  job_title: string;
  company_name: string;
  company_key: string;
  source: string;
  answers: Record<string, unknown>;
  client_id: string;
  created_at: string;
  speaks_to: SpeaksTo[];
}

/* Several individuals from one employer, read together. One person is a lead;
   eight of them are a picture of a company nobody has sold to yet. */
export interface ProspectCompany {
  company_key: string;
  company_name: string;
  /* Set once a partner says which of their clients these people work for. Until
     then they are individuals who answered, which is the state the intake is
     designed to start in rather than a gap in the data. */
  client_id: string | null;
  people: number;
  roles: string[];
  tools: { tool: string; people: number }[];
  latest: string;
  members: {
    id: string; name: string; email: string; job_title: string;
    speaks_to: SpeaksTo[];
  }[];
  /** The company's own questions this room can already answer between them. */
  covered: { section: string; label: string; people: number }[];
}

/* A file the client's workspace already holds. It stays the builder's: this is
   what the importer offers so a partner does not have to download it there and
   upload it here, which was the whole round trip. */
export interface WorkspaceDocument {
  id: string;
  filename: string;
  mime: string | null;
  bytes: number | null;
  updated_at: string;
  origin: string;
}

export interface StarterDeckCard { key: string; section: string; label: string; kind: string; options: { value: string; label: string }[]; why: string; placeholder: string; rank: number; }

export type IntakeKind = 'text' | 'one' | 'many' | 'number';

export interface IntakeQuestion {
  id: string;
  label: string;
  kind?: IntakeKind;
  options?: string[];
  /** Can be skipped. Opening questions never are. */
  optional?: boolean;
  /** Only on a member's own copy: what they answered. */
  answer?: string;
  /** Only on a member's own copy: what Orbit filled, waiting for them to confirm. */
  filled?: string;
  filled_note?: string;
}

export type IntakeTab = 'opening' | 'business' | 'department' | 'person' | 'tool';

export interface IntakeSection {
  id: string;
  scope: string;
  title: string;
  description: string;
  questions: IntakeQuestion[];
  position: number;
  /** Set by Agents Anywhere for every partner: never moved, edited or deleted by one. */
  fixed: boolean;
  timing: 'setup' | 'later' | 'session';
  /** Who at the client receives it, by role. Fixed sections and sessions are 'everyone'. */
  audience: IntakeAudience;
  /** Set on a session: the one member it was sent to, and when. */
  member_email?: string | null;
  sent_at?: string | null;
  /** Set on a session once its person submitted it from the AI Companion. */
  answered_at?: string | null;
  intake?: IntakeTab;
  /** 'tool': asked once per tool the person uses. 'department': about their department. */
  repeat?: '' | 'tool' | 'department';
  /** On a partner's view of our section: they removed all of it for their clients. */
  removed?: boolean;
  /** On a partner's view of our section: they made their own version of its questions. */
  customised?: boolean;
  /** Only on a member's own copy: the tool or department this copy is about. */
  subject?: string;
  subject_label?: string;
  key?: string;
}

/** 'everyone', or a comma list of owners, heads and employees. */
export type IntakeAudience = string;
export type IntakeGroup = 'owners' | 'heads' | 'employees';

export interface IntakeSectionsView {
  scope: string;
  intakes?: IntakeTab[];
  fixed: IntakeSection[];
  sections: IntakeSection[];
  clients: number;
}

export interface IntakeSectionBody {
  title: string;
  description: string;
  questions: { id?: string; label: string; kind?: IntakeKind; options?: string[]; optional?: boolean }[];
  audience?: IntakeAudience;
  intake?: IntakeTab;
  repeat?: string;
}

export interface IntakeJourneySection extends IntakeSection {
  /** How many people at the client this section goes to. */
  receivers: number;
  /** How many of them answered every question in it. */
  done: number;
}
export interface IntakeJourney {
  people: number;
  sections: IntakeJourneySection[];
}
export interface IntakePersonProgress {
  step: string | null;
  finished: boolean;
  apps: number;
  answered: number;
  total: number;
}

export interface IntakePersonDetail {
  email: string;
  setup: { started: boolean; step: string | null; skipped: string[]; finished_at: string | null; updated_at: string | null };
  apps: { slug: string; label: string; logo?: string }[];
  sections: IntakeSection[];
  answered: number;
  total: number;
}

/* Partner sessions: questionnaires and surveys built with the session agent. */
export type SessionKind = 'questionnaire' | 'survey';
export type SessionQuestionType = 'text' | 'single' | 'multi' | 'scale' | 'tools' | 'file' | 'secret';
/** What a session is for; a project or Quick Win session names which one. */
export type SessionPurpose = 'project' | 'quick_win' | 'general' | 'context';
/** A key answer as the partner sees it: never the value, only a hint and the Vault status. */
export interface SessionKeyAnswer {
  hint: string;
  length: number;
  status?: 'queued' | 'pending' | 'set' | 'rejected';
  status_label?: string;
  reason?: string;
  env_name?: string;
}
export interface SessionProjectNeed {
  key: string;
  label: string;
  kind: string;
  status: string;
  audience: string;
  purpose?: string;
}
export interface SessionTargets {
  projects: { id: string; title: string; status: string; goal: string; needs: SessionProjectNeed[] }[];
  quick_wins: { id: string; title: string; published: boolean; problem: string }[];
}
/** A document uploaded to answer a file question. */
export interface SessionFileAnswer {
  id: string;
  name: string;
  size: number;
  type: string;
}
/** An app picked from the logo wall. */
export interface SessionApp {
  slug: string;
  label: string;
  logo?: string;
}
export interface SessionQuestion {
  id: string;
  label: string;
  type: SessionQuestionType;
  /** single and multi: 2 to 12 options. */
  options?: string[];
  /** scale: the labels for 1 and 5. */
  low?: string;
  high?: string;
  /** secret: the environment variable the key is stored as in the project's Vault. */
  env_name?: string;
  /** secret, for the person answering: how the key they gave last time went. */
  key?: Pick<SessionKeyAnswer, 'status' | 'status_label' | 'reason'>;
}
export interface PartnerSessionSummary {
  id: string;
  client_id: string;
  title: string;
  description: string;
  kind: SessionKind;
  questions: SessionQuestion[];
  status: 'draft' | 'sent';
  sent_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  recipients: number;
  answered: number;
  purpose: SessionPurpose;
  target_id: string;
  target_title: string;
  remind_every_days: number | null;
}
export interface PartnerSessionFile {
  id: string;
  name: string;
  mime: string;
  size: number;
  chars: number;
  created_at: string | null;
}
export interface PartnerSessionMessage {
  role: 'user' | 'assistant';
  content: string;
}
export interface PartnerSessionDetail extends PartnerSessionSummary {
  thread: PartnerSessionMessage[];
  files: PartnerSessionFile[];
}
export interface SessionPerson {
  email: string;
  name: string;
  role: string;
  department: string;
  department_name: string;
  onboarded: boolean;
}
export interface SessionTally {
  options?: { label: string; count: number }[];
  average?: number | null;
  spread?: { score: number; count: number }[];
  apps?: (SessionApp & { count: number })[];
}
export type SessionAnswerValue = string | string[] | number | SessionApp[] | SessionFileAnswer[] | SessionKeyAnswer | null;
export interface SessionResponseQuestion extends SessionQuestion {
  tally: SessionTally;
}
export interface SessionRecipient {
  email: string;
  name: string;
  role: string;
  department: string;
  sent_at: string | null;
  answered_at: string | null;
  answers: Record<string, SessionAnswerValue>;
}
export interface SessionResponses {
  session: Omit<PartnerSessionSummary, 'questions'> & { questions: SessionResponseQuestion[] };
  recipients: SessionRecipient[];
}
/** One session sent to one person, with their answers, for their person page. */
export interface PersonSession {
  id: string;
  title: string;
  kind: SessionKind;
  sent_at: string | null;
  answered_at: string | null;
  questions: (SessionQuestion & { answer: SessionAnswerValue | null; text: string })[];
}
export type SessionAgentEvent =
  | { type: 'status'; text: string }
  | { type: 'text'; delta: string }
  | { type: 'draft'; draft: PartnerSessionDetail }
  | { type: 'done'; message: string }
  | { type: 'error'; message: string };
